/**
 * English interface texts.
 *
 * Typed against `Translations` — a missing key is a compile error, not a
 * silently empty label. The key set is defined by `de.ts`.
 *
 * WHAT IS NOT TRANSLATED HERE: business data. Product categories, product
 * families, article numbers, descriptions and supplier names come from the
 * database and are shown unchanged in both languages. Only interface and
 * system texts are translated, plus the labels of technical lookup codes.
 *
 * Terminology follows the project knowledge base: "Supplier" stays
 * "Supplier" — neither "vendor" nor "provider" is introduced here.
 */
import type { Translations } from "./de.ts";
export const en: Translations = {
  // ── General ────────────────────────────────────────────────────────────
  "common.loading": "Loading …",
  "common.errorTitle": "An error occurred",
  "common.retry": "Try again",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.none": "none",
  "common.unknownValue": "not determined",
  "common.unknownValueTitle": "Value not determined",
  // ── Language switch ────────────────────────────────────────────────────
  "language.label": "Language",
  "language.toGerman": "Switch to German",
  "language.toEnglish": "Switch to English",
  // ── Sign in ────────────────────────────────────────────────────────────
  "login.title": "Sign in",
  "login.username": "Username",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.submitBusy": "Signing in …",
  "login.foot": "Internal application. Authorized users only.",
  "login.passwordChanged": "Your password has been changed. Please sign in with your new password.",
  // ── Frame ──────────────────────────────────────────────────────────────
  "app.checkingSession": "Checking session …",
  "app.loadingMeta": "Loading master data …",
  "nav.home": "To the product list",
  "nav.sections": "Sections",
  "nav.products": "Products",
  "nav.suppliers": "Business Partners",
  "sup.uebersicht": "Overview",
  "sup.stammdaten": "Master data",
  "sup.produkte": "Products",
  "sup.datenbedarf": "Data needs",
  "sup.konditionen": "Terms",
  "sup.add": "+ Partner",
  "sup.addTitle": "Add a business partner",
  "sup.addSave": "Create partner",
  "sup.addHint": "Short name, legal name, role and status are enough. Everything else is added in the file — a supplier does not need an article to exist.",
  "sup.legalNameOptional": "Legal company name (optional)",
  "sup.status": "Status",
  "sup.address": "Address",
  "sup.addressLine1": "Address line 1",
  "sup.addressLine2": "Address line 2",
  "sup.addressLine2Hint": "Building, hall, district",
  "sup.countryNone": "no country selected",
  "sup.editTermsAria": "Edit terms",
  "sup.editingTerms": "Editing terms",
  "sup.groupFrame": "General terms",
  "sup.frameLater": "Incoterm and bank details follow in their own blocks.",
  "sup.groupMissing": "Missing supplier master data",
  "sup.missingScope": "This list covers supplier master data only — not product knowledge.",
  "sup.sortBy": "Sort by {feld}",
  "supStatus.active": "Active supplier",
  "supStatus.backup": "Backup supplier",
  "supStatus.sourcing": "Product sourcing",
  "supStatus.inactive": "Inactive",
  "error.family_code_taken": "This family code is already in use.",
  "error.parameter_set_superseded": "This parameter set is superseded. A new value belongs in a new version.",
  "error.parameter_set_date_taken": "A parameter set already exists for this valid-from date. Please choose another date.",
  "error.parameter_set_locked": "A parameter set already used in calculations exists for this valid-from date. Please choose another date.",
  "error.family_incomplete": "A product family needs a category, a code and a name.",
  "error.invalid_category": "This main category does not exist.",
  "error.invalid_country": "The country is kept as an ISO 3166-1 code, for example CN or DE.",
  "error.unknown_status": "This supplier status is unknown.",
  "error.supplier_exists": "A supplier with this name already exists.",
  "error.legal_equals_name": "The legal name is not the short name. Leave it empty if you do not know it.",
  "error.messenger_incomplete": "Another contact channel needs both a kind and a handle — or neither.",
  "error.name_required": "The name is missing.",
  "error.unknown_role": "This responsibility is not in the catalogue.",
  "error.invalid_date": "Please enter a date as YYYY-MM-DD.",
  "error.number_exists": "This supplier number is already taken.",
  "error.primary_exists": "There already is a main contact.",
  "error.supplier_merged": "This supplier was merged and is no longer maintained.",
  "sup.title": "Business Partners",
  "sup.search": "Search partner",
  "sup.count": "{n} partners",
  "sup.name": "Short name",
  "sup.shortName": "Short name",
  "sup.legalName": "Legal company name",
  "sup.supplierNumber": "Supplier number",
  "sup.optional": "optional",
  "sup.country": "Country",
  "sup.street": "Street",
  "sup.postalCode": "Postal code",
  "sup.city": "City",
  "sup.province": "Province / state",
  "sup.website": "Website",
  "sup.articles": "Articles",
  "sup.rating": "Rating",
  "sup.state": "Status",
  "sup.activeYes": "active",
  "sup.inactive": "inactive",
  "sup.missing": "{n} missing",
  "sup.back": "Back to list",
  "sup.partnerSince": "Partner since",
  "sup.createdAt": "Created in HOMEBASE",
  "sup.editAria": "Edit master data",
  "sup.editing": "Editing master data",
  "sup.editHint": "Typed entries apply only after saving. The short name is not changed here.",
  "sup.groupPartner": "Partner",
  "sup.groupContact": "Main contact",
  "sup.groupBusiness": "Business relationship",
  "sup.groupOpen": "What is missing",
  "sup.groupCompany": "Company",
  "sup.groupAddress": "Address",
  "sup.groupContacts": "Contacts",
  "sup.groupTerms": "Payment terms",
  "sup.nothingMissing": "Everything HOMEBASE knows today is recorded.",
  "sup.noPrimary": "No main contact recorded.",
  "sup.noContacts": "No contact recorded yet.",
  "sup.noPrimaryAmong": "{n} contacts recorded, but none of them is the main contact.",
  "sup.goMaintain": "maintain",
  "sup.noArticles": "No article is assigned to this supplier.",
  "sup.contacts": "Contacts",
  "sup.contactName": "Name",
  "sup.contactJob": "Job title",
  "sup.contactRole": "Responsibility",
  "sup.contactEmail": "Email",
  "sup.contactPhone": "Phone",
  "sup.contactMobile": "Mobile",
  "sup.messengerKind": "Other channel",
  "sup.messengerNone": "no other channel",
  "sup.messengerId": "Handle",
  "sup.roleNone": "no responsibility",
  "sup.primary": "Main contact",
  "sup.makePrimary": "make main contact",
  "sup.removeContact": "remove",
  "sup.addContact": "Add contact",
  "sup.saveContact": "Save contact",
  "sup.articleNumber": "Article number",
  "sup.articleName": "Description",
  "sup.alsoFrom": "also from {n} more",
  "sup.terms": "Payment plan",
  "sup.ownTerms": "own agreement",
  "sup.standardTerms": "Supplier standard",
  "sup.paymentTerms": "Payment terms",
  "sup.paymentStandard": "Supplier standard",
  "sup.termsOpen": "Unsaved changes",
  "sup.termsHint": "Rating and payment plan are saved together.",
  "sup.overrideHint": "Deviating agreements for individual articles are maintained on the article, not here.",
  "supRole.sales": "Sales",
  "supRole.engineering": "Engineering",
  "supRole.quality": "Quality / QC",
  "supRole.finance": "Finance",
  "supRole.logistics": "Logistics",
  "supRole.management": "Management",
  "messenger.wechat": "WeChat",
  "messenger.whatsapp": "WhatsApp",
  "messenger.skype": "Skype",
  "messenger.teams": "Teams",
  "sup.firstName": "First name",
  "sup.lastName": "Last name",
  "sup.contactRoles": "Responsibilities",
  "sup.contactDepartment": "Department",
  "sup.contactNote": "Note",
  "sup.contactActive": "active",
  "sup.contactInactive": "no longer active",
  "sup.contactsMore": "and {n} more",
  "sup.editContactAria": "Edit {name}",
  "sup.removeSure": "Really remove?",
  "sup.removeYes": "remove",
  "sup.preferredChannel": "Preferred channel",
  "sup.preferredNone": "no preferred channel",
  "sup.incoterm": "Incoterm",
  "sup.incotermNone": "no delivery term",
  "sup.currency": "Preferred currency",
  "sup.currencyNone": "no currency",
  "sup.groupQuality": "Data quality",
  "sup.openNeed": "Open supplier data need",
  "sup.openNeedValue": "{q} open questions on {a} products",
  "sup.noOpenNeed": "no open questions",
  "sup.bankCurrency": "Currency",
  "supRole.product_development": "Product development",
  "channel.email": "Email",
  "channel.phone": "Phone",
  "channel.mobile": "Mobile",
  "channel.messenger": "Messenger",
  "sup.finanzen": "Finance",
  "sup.groupNotes": "Notes",
  "sup.addNote": "+ Add note",
  "sup.noNotes": "No note recorded yet.",
  "sup.noteText": "Note",
  "sup.notePlaceholder": "What should be recorded about this business relationship?",
  "sup.noteEdited": "edited on {datum} by {wer}",
  "sup.editNoteAria": "Edit note",
  "sup.groupBank": "Bank accounts",
  "sup.addBank": "+ Add bank account",
  "sup.noBank": "No bank account recorded for this supplier.",
  "sup.bankLabel": "Label",
  "sup.bankLabelHint": "e.g. USD Account – Bank of China",
  "sup.beneficiary": "Beneficiary",
  "sup.accountNumber": "Account number",
  "sup.iban": "IBAN",
  "sup.bankName": "Bank",
  "sup.bankAddress": "Bank address",
  "sup.bankCountry": "Bank country",
  "sup.swift": "SWIFT / BIC",
  "sup.branch": "Branch",
  "sup.branchHint": "Name or code",
  "sup.clearing": "Local clearing code",
  "sup.clearingHint": "CNAPS, ABA, sort code",
  "sup.correspondent": "Correspondent bank",
  "sup.payHint": "Payment instructions",
  "sup.bankPrimary": "Default",
  "sup.bankInactive": "inactive",
  "sup.makeBankPrimary": "make default",
  "sup.deactivate": "deactivate",
  "sup.activate": "reactivate",
  "sup.editBankAria": "Edit {name}",
  "sup.bankScope": "Bank accounts are supplier master data. Orders, invoices and payments are not created here.",
  "sup.bankSummary": "Bank accounts",
  "sup.bankSummaryValue": "{n} · {waehrungen}",
  "nav.procurement": "Production & logistics",
  "plp.title": "Production & logistics",
  "plp.count": "{n} purchase orders",
  "plp.zoom": "Time scale",
  "plp.timeline": "Timeline",
  "plp.timelineScroll": "Timeline — scrolls horizontally",
  "plp.calendarWeek": "W{n}",
  "plp.timelineHint": "Real time axis — an event sits where its date is.",
  "plp.today": "Today",
  "plp.noLanes": "No purchase order recorded yet.",
  "plp.back": "Back to overview",
  "plp.addOrderShort": "+ Purchase order",
  "plp.addCn": "+ Consolidation",
  "plp.newOrder": "Take over a purchase order from DTE",
  "plp.chooseSupplier": "Choose supplier",
  "plp.numberExample": "PO70305",
  "plp.dteNumber": "DTE order number",
  "plp.numberHint": "Format checked. NOT verified against DTE — HOMEBASE never issues order numbers.",
  "plp.orderedAt": "Document date",
  "plp.note": "Note",
  "plp.order": "Purchase order",
  "plp.orders": "Purchase orders",
  "plp.lines": "Lines",
  "plp.noLines": "No line recorded yet.",
  "plp.addLine": "+ Line",
  "plp.article": "Article",
  "plp.chooseArticle": "Choose article",
  "plp.quantity": "Quantity",
  "plp.unitPrice": "Agreed price",
  "plp.moq": "below MOQ",
  "plp.belowMoq": "Minimum order quantity {n}",
  "plp.leadTime": "Lead time rule",
  "plp.noRule": "No lead time rule applies to this order. Without one there is no forecast.",
  "plp.duration": "Duration",
  "plp.days": "{min}–{max} days",
  "plp.daysMin": "At least (days)",
  "plp.daysMax": "At most (days)",
  "plp.anchor": "Anchor",
  "plp.anchorMissing": "date missing",
  "china.track": "CHINA",
  "china.editAria": "Edit the China planning layer",
  "china.title": "China · planning layer",
  "china.hint": "External context. It does not change any lead time or date.",
  "china.buffer": "Lead-in {vor} days · recovery {nach} days",
  "china.core": "Core event",
  "china.leadIn": "Days before",
  "china.leadOut": "Days after",
  "china.name": "Name",
  "china.from": "Start",
  "china.to": "End",
  "china.sourceNote": "Source",
  "china.checkedOn": "last checked",
  "china.add": "+ Event",
  "china.deactivate": "Deactivate",
  "china.inactive": "deactivated",
  "china.none": "No event recorded for this period.",
  "china.detailTitle": "China event",
  "chinaStatus.bestaetigt": "officially confirmed",
  "chinaStatus.vorlaeufig": "preliminary",
  "plp.anchorValueIncomplete": "order value incomplete",
  "payAnchor.deposit_paid": "Deposit paid",
  "payAnchor.paid_in_full": "Paid in full",
  "plp.scope": "Scope",
  "plp.source": "Source",
  "plp.quoted": "Wording",
  "plp.quotedHint": "e.g. 5-6 weeks after payment confirmed",
  "plp.milestones": "Milestones",
  "plp.milestone": "Milestone",
  "plp.noMilestones": "No milestone recorded yet.",
  "plp.chooseMilestone": "Choose milestone",
  "plp.date": "Date",
  "plp.certainty": "Date status",
  "plp.whyMoved": "Why is the date moving?",
  "plp.whyMovedHint": "short reason — cannot be reconstructed later",
  "plp.moveMilestone": "Move date",
  "plp.removeMilestone": "remove",
  /* Editing and removing are two actions: one corrects, one retracts. */
  /* Eine Zahlung ist per Definition geleistet -- sie braucht
     keinen Terminstatus und keine Zahlungsart. */
  "plp.payment": "Payment",
  "plp.editMilestone": "Edit milestone",
  "plp.editingMilestone": "Editing {was} of {wann}. A changed date supersedes the current entry — it stays readable as history.",
  "plp.superseded": "superseded",
  "plp.previously": "previously {wann}",
  "plp.review": "Closing review",
  "plp.reviewStars": "Rating",
  "plp.reviewStarsOf": "{n} out of 5 stars",
  "plp.reviewNone": "not yet rated",
  "plp.reviewClear": "clear",
  "plp.reviewNote": "Review / anything notable",
  "plp.reviewNoteHint": "What went particularly well, badly or unusually?",
  "plp.notMeasurable": "not measurable",
  "plp.dOrdered": "Ordered",
  "plp.dReady": "Ready for shipment",
  "plp.dOrderToReady": "Order to readiness",
  "plp.leadTimeSection": "Lead time",
  "plp.dWaitForOthers": "Waiting for the remaining orders",
  "plp.dFirstAtForwarder": "First order at the forwarder",
  "plp.dLastAtForwarder": "Last order at the forwarder",
  "plp.dDays": "{n} days",
  "plp.dFairness": "The waiting time belongs to the consolidation, not to the order — an order that was ready on time is not marked down for it.",
  "plp.openProcessData": "Open process entries",
  "plp.stillRequired": "Still required",
  "plp.openTransportData": "Transport entries not yet recorded",
  "plp.notEveryStepRequired": "Not every order needs every step — these entries simply have not been recorded yet.",
  "plp.recordedBy": "recorded by {wer} on {wann}",
  "plp.attention": "What needs attention?",
  "plp.severityUrgent": "Urgent",
  "plp.severityOpen": "Open",
  "plp.window": "Consolidation window",
  "plp.expectedReady": "Expected ready",
  "plp.cutoff": "Cutoff",
  "plp.bufferLabel": "Planning buffer",
  "plp.buffer": "{n} days",
  "plp.windowSentence": "{po} could still make it into {cn} in time. A human decides on the assignment.",
  "plp.assign": "assign",
  "plp.unassign": "Remove from CN",
  "plp.cnMaster": "Consolidation data",
  "plp.ordersCount": "{n} orders",
  "plp.atForwarderCount": "{da}/{von} at the forwarder",
  "plp.transportTitle": "Transport",
  "plp.transportMode": "Mode of transport",
  "plp.transportUnknown": "not decided yet",
  "plp.allArrived": "All orders have arrived at the forwarder",
  "plp.arrivedOf": "{da} of {alle} orders at the forwarder",
  "plp.historyTitle": "Released orders",
  "plp.atForwarderOn": "at the forwarder since {date}",
  "plp.notAtForwarder": "still outstanding",
  "plp.markAtForwarder": "Record arrival at forwarder",
  "plp.unmarkAtForwarder": "Undo arrival",
  "plp.inTransitHint": "This consolidation has been handed to the carrier. Orders can no longer be released.",
  "plp.releaseFrom": "Release {po} from {cn}",
  "plp.releaseReason": "Reason",
  "plp.releaseReasonPlaceholder": "e.g. production delay",
  "plp.releasedOrders": "Released orders",
  "plp.releasedOn": "released on {date}",
  "plp.noActiveCn": "no active consolidation",
  "plp.heldBack": "held back for the next order run",
  "plp.addOrder": "Assign purchase order",
  "plp.chooseOrder": "Choose purchase order",
  "plp.cnEmpty": "No purchase order assigned to this consolidation yet.",
  "plp.closeCn": "Close consolidation",
  "plp.discardCn": "Discard CN",
  "plp.deviation": "Deviation {n} days",
  "plp.wasPromised": "previously promised: {datum}",
  "plp.basisRule": "Basis: {min}–{max} days from {anker} {datum} · {bereich} · {quelle}",
  "plp.basisOrders": "Basis: the {n} assigned purchase orders",
  "plp.missingRule": "no lead time rule",
  "plp.missingAnchor": "anchor {anker} has no date",
  "plp.moqLines": "{n} line(s)",
  "ms.po_ordered": "Ordered",
  "ms.pi_received": "PI received",
  "ms.pi_approved": "PI approved",
  "ms.deposit_due": "Deposit due",
  "ms.deposit_paid": "Deposit paid",
  "ms.artwork_sent": "Artwork sent",
  "ms.artwork_approved": "Artwork approved",
  "ms.production_start": "Production start",
  "ms.production_ready": "Production ready",
  "ms.qc_planned": "Inspection planned",
  "ms.qc_passed": "Inspection passed",
  "ms.qc_not_required": "QC not required",
  "ms.ready_for_shipment": "Ready for shipment",
  "ms.consolidation_cutoff": "Cutoff",
  "ms.consolidation_ready": "Consolidation ready",
  "ms.planned_etd": "ETD",
  "cert.forecast": "Forecast",
  "cert.confirmed": "Confirmed",
  "cert.actual": "Actual",
  "poStatus.offen": "open",
  "poStatus.geschlossen": "closed",
  "poStatus.storniert": "cancelled",
  "cnStatus.geplant": "planned",
  "cnStatus.gebucht": "booked",
  "cnStatus.in_transport": "in transit",
  "cnStatus.zugestellt": "delivered",
  "cnStatus.geschlossen": "closed",
  "cnStatus.verworfen": "discarded",
  "cnTransport.sea": "Sea freight",
  "cnTransport.air": "Air freight",
  "cnTransport.train": "Rail",
  "cnTransport.road": "Road",
  "cnTransport.multimodal": "Multimodal",
  "cnTransport.other": "Other",
  "plpScope.supplier": "Supplier",
  "plpScope.supplier_article": "Relation",
  "plpScope.purchase_order": "this order",
  "plpSource.supplier_pi": "Supplier proforma",
  "plpSource.supplier_statement": "Supplier statement",
  "plpSource.internal_assumption": "Internal assumption",
  "plpOrigin.manual": "entered manually",
  "plpOrigin.dte_feed": "from the DTE feed",
  "plpZoom.tag": "Days",
  "plpZoom.woche": "Weeks",
  "plpZoom.monat": "Months",
  "plpWindow.passt": "fits",
  "plpWindow.passt_knapp": "tight",
  "plpWindow.passt_nicht": "does not fit",
  "plpWindow.unbestimmt": "undetermined",
  "plpAttn.po_without_cn": "not assigned to a consolidation",
  "plpAttn.po_forecast_basis_missing": "forecast basis missing",
  "plpAttn.po_ready_missing": "no date for readiness",
  "plpAttn.po_pi_overdue": "no PI received since the order",
  "plpAttn.po_deposit_overdue": "deposit due but not paid",
  "plpAttn.po_artwork_open": "artwork sent but not approved",
  "plpAttn.po_step_open": "a required process step is still missing",
  "plpAttn.po_qc_missing": "production finishing but no inspection planned",
  "plpAttn.po_below_moq": "quantity below the minimum order quantity",
  "plpAttn.milestone_overdue": "confirmed date passed, no actual recorded",
  "plpAttn.cn_without_po": "no purchase order assigned yet",
  "plpAttn.cn_po_after_cutoff": "order will only be ready after the cutoff",
  "plpAttn.cn_window_open": "another purchase order could fit in time",
  "plpAttnDo.po_without_cn": "Assign to a consolidation.",
  "plpAttnDo.po_forecast_basis_missing": "Add a lead time rule.",
  "plpAttnDo.po_ready_missing": "Record readiness for shipment.",
  "plpAttnDo.po_pi_overdue": "Request the PI from the supplier.",
  "plpAttnDo.po_deposit_overdue": "Record the payment under Finance.",
  "plpAttnDo.po_artwork_open": "Record the artwork approval.",
  "plpAttnDo.po_step_open": "Record it in the file under “Still required”.",
  "plpAttnDo.po_qc_missing": "Record an inspection date.",
  "plpAttnDo.po_below_moq": "Check the quantity or the minimum order quantity.",
  "plpAttnDo.milestone_overdue": "Record what actually happened — or move the commitment.",
  "plpAttnDo.cn_without_po": "Assign a purchase order.",
  "plpAttnDo.cn_po_after_cutoff": "Check the assignment or the cut-off.",
  "plpAttnDo.cn_window_open": "Check the assignment.",
  "supMissing.legalName": "legal company name",
  "supMissing.country": "country",
  "supMissing.address": "address",
  "supMissing.partnerSince": "partner since",
  "supMissing.contacts": "contacts",
  "nav.users": "Users",
  "nav.nora": "Nora HR",
  "nav.signOut": "Sign out",
  "nav.changePassword": "Change password",
  "nav.roleTitle": "Role: {role}",
  // ── User roles ─────────────────────────────────────────────────────────
  "userRole.admin": "Administrator",
  "userRole.viewer": "Viewer",
  "userRole.product": "Product management",
  "userRole.marketing": "Marketing",
  "userRole.connector": "Connector",
  // ── Article list ───────────────────────────────────────────────────────
  "list.searchPlaceholder": "Search article number or description …",
  "list.search": "Search",
  "list.category": "Product category",
  "list.family": "Product family",
  "list.role": "Article role",
  "list.allCategories": "All categories",
  "list.allFamilies": "All families",
  "list.allRoles": "All roles",
  "list.lifecycle": "Assortment status",
  "list.lifecycleAll": "Any assortment status",
  "list.lifecycleOpen": "not yet determined",
  "list.lifecycleUnset": "Not determined",
  "list.availability": "Availability",
  "list.availabilityAll": "Any availability",
  "list.availabilityYes": "Available",
  "list.availabilityNo": "Not available",
  "list.availabilityUnknown": "No stock statement",
  "list.operational": "Operational state",
  "list.operationalAll": "Any state",
  "list.operationalStock": "In stock",
  "list.operationalIncoming": "Incoming",
  "list.operationalOffered": "Offered",
  "list.statusSuspended": "The status filter is not in effect yet: no stock or procurement source is connected. Until then every article's state is undetermined, and the list shows the entire stock.",
  "list.resetFilters": "Reset filters",
  "list.articles": "Articles",
  "list.ofTotal": "of {total}",
  "list.emptyTitle": "No articles found",
  "list.emptyHint": "Adjust the search term or the filters.",
  "list.colNumber": "Article number",
  "list.colDescription": "Description",
  "list.colCategory": "Category",
  "list.colFamily": "Product family",
  "list.colStatus": "Status",
  "list.colRoles": "Roles",
  "list.sortBy": "Sort by {label}",
  "list.prev": "Previous",
  "list.next": "Next",
  "list.page": "Page {page} of {total}",
  // ── Badges ─────────────────────────────────────────────────────────────
  "badge.primaryFamily": "primary product family",
  "badge.secondaryFamily": "additional family relation",
  "badge.openFlags": "{count} open",
  "badge.openFlagsTitle": "{count} open review flags",
  // ── Article detail ─────────────────────────────────────────────────────
  "detail.aria": "Article detail",
  "detail.category": "Product category",
  "detail.quality": "Data quality",
  "detail.openFlags": "Open review flags",
  "detail.compatibleWith": "compatible with",
  "detail.legacy": "Historical classification",
  "detail.articleNumbers": "Article numbers",
  // ── User administration ────────────────────────────────────────────────
  "users.title": "Users",
  "users.loading": "Loading users …",
  "users.create": "Create user",
  "users.hint": "An {admin} may do everything. A {viewer} may only read – view, search and filter articles. Write access is refused by the server, regardless of what is shown here.",
  "users.footnote": "Your own account can neither be deactivated nor demoted, and the last active administrator always remains. Deactivating an account, changing its role and setting a password immediately end all existing sessions.",
  "users.colUsername": "Username",
  "users.colName": "Display name",
  "users.colRole": "Role",
  "users.colStatus": "Status",
  "users.colLastLogin": "Last sign-in",
  "users.colSessions": "Sessions",
  "users.colActions": "Actions",
  "users.active": "active",
  "users.inactive": "deactivated",
  "users.self": "(you)",
  "users.roleAria": "Role of {user}",
  "users.noRole": "— none —",
  "users.setPassword": "Set password",
  "users.deactivate": "Deactivate",
  "users.activate": "Activate",
  "users.never": "—",
  "users.formUsername": "Username",
  "users.formUsernameHint": "e.g. mira",
  "users.formDisplayName": "Display name",
  "users.formDisplayNameHint": "First and last name",
  "users.formRole": "Role",
  "users.formPassword": "Initial password",
  "users.formPasswordHint": "at least {n} characters",
  "users.formSubmit": "Create",
  "users.formSubmitBusy": "Creating …",
  "users.msgCreated": "Account {user} has been created.",
  "users.msgRoleChanged": "{user} now has the role {role}.",
  "users.msgPasswordSet": "The password for {user} has been reset.",
  "users.msgDeactivated": "{user} has been deactivated.",
  "users.msgActivated": "{user} is active again.",
  "users.promptPassword": "New password for “{name}” (at least {n} characters).\nExisting sessions will be ended.",
  // ── Change own password ────────────────────────────────────────────────
  "pw.title": "Change password",
  "pw.intro": "After the change all existing sessions are ended. You then sign in again with the new password.",
  "pw.current": "Current password",
  "pw.new": "New password",
  "pw.repeat": "Confirm password",
  "pw.rule": "at least {n} characters",
  "pw.tooShort": "Still too short.",
  "pw.mismatch": "The two entries do not match.",
  "pw.submit": "Change password",
  "pw.submitBusy": "Changing …",
  // ── Error texts by API error code ──────────────────────────────────────
  /* Correction run 005 -- see the German file. */
  "error.already_applied": "This proposal has already been applied.",
  "error.already_clarified": "This value has already been clarified.",
  "error.already_decided": "A decision has already been made here.",
  "error.already_resolved": "This finding has already been resolved.",
  "error.bad_code": "This code has an invalid form.",
  "error.bad_group": "HOMEBASE does not know this group.",
  "error.bad_value_type": "This value does not match the property type.",
  "error.bank_incomplete": "This bank account is missing a required entry.",
  "error.bank_primary_exists": "There is already a default account for this currency.",
  "error.below_minimum_margin": "This price is below the minimum margin.",
  "error.category_code_taken": "This category code already exists.",
  "error.category_incomplete": "This category is missing a required entry.",
  "error.code_locked": "This code is locked and can no longer be changed.",
  "error.code_taken": "This code already exists.",
  "error.computed_column": "This value is computed and cannot be set by hand.",
  "error.definition_in_use": "This definition is in use and cannot be removed.",
  "error.definition_incomplete": "This definition is missing a required entry.",
  "error.duplicate_level": "This level already exists in this series.",
  "error.duplicate_material": "This material is already in the list.",
  "error.empty_customs_fact": "This customs entry has no value.",
  "error.empty_proposal": "This proposal contains nothing to apply.",
  "error.enum_not_supported": "This property type does not support a value list.",
  "error.evidence_exists": "Evidence already exists for this finding.",
  "error.execution_taken": "This variant already exists in this product model.",
  "error.flag_open": "There is still an open finding on this record.",
  "error.handoff_not_configured": "This handoff has not been set up.",
  "error.in_trash": "This record is in the trash.",
  "error.invalid_code": "This code has an invalid form.",
  "error.invalid_count": "This count is not a valid number.",
  "error.invalid_flag": "HOMEBASE does not know this finding.",
  "error.invalid_level": "This level is invalid.",
  "error.invalid_material": "HOMEBASE does not know this material.",
  "error.invalid_result": "HOMEBASE does not know this test result.",
  "error.invalid_test_type": "HOMEBASE does not know this test type.",
  "error.invalid_measure": "This measurement is not a valid value.",
  "error.packaging_material_mismatch": "The material totals do not match the computed packaging weights. A packaging record only becomes reliable once every level adds up exactly.",
  "error.invalid_net_weight": "This net weight is not a valid value.",
  "error.invalid_operator": "HOMEBASE does not know this comparison.",
  "error.invalid_origin_country": "A country of origin is two capital letters.",
  "error.invalid_parameter": "This parameter value is invalid.",
  "error.invalid_payment_amount": "A payment amount is a number greater than zero.",
  "error.invalid_relation": "This relation is invalid.",
  "error.invalid_roles": "This set of roles is invalid.",
  "error.invalid_standard": "This standard is invalid.",
  "error.invalid_supplier": "This supplier does not exist.",
  "error.invalid_swift": "A SWIFT/BIC has eight or eleven letters and digits.",
  "error.invalid_target": "This target is invalid.",
  "error.invalid_tariff": "This customs tariff number is invalid.",
  "error.invalid_tariff_code": "This customs tariff number is invalid.",
  "error.invalid_transport_mode": "HOMEBASE does not know this mode of transport.",
  "error.minimum_margin_forbidden": "A minimum margin is not provided for here.",
  "error.model_release_conflicts": "The release conflicts with existing variants.",
  "error.no_article": "There is no article for this.",
  "error.no_family": "There is no product family for this.",
  "error.no_model": "There is no product model for this.",
  "error.no_model_value": "There is no shared model value for this.",
  "error.no_parameter_set": "There is no parameter set for this.",
  "error.no_rule": "There is no rule for this.",
  "error.no_such_finding": "This finding does not exist.",
  "error.no_template": "There is no template for this.",
  "error.not_a_variant": "This property is not a variant axis.",
  "error.not_in_trash": "This record is not in the trash.",
  "error.not_latest_revision": "This is not the current revision. Please reload first.",
  "error.note_empty": "A note without text is not a note.",
  "error.nothing_selected": "Nothing is selected.",
  "error.operator_not_allowed": "This comparison is not allowed here.",
  "error.payment_amount_required": "A payment needs an amount.",
  "error.payment_currency_required": "A payment needs a currency.",
  "error.preferred_channel_missing": "The preferred channel is not recorded for this contact.",
  "error.primary_inactive": "An inactive entry cannot be the default.",
  "error.relation_in_use": "This relation is in use and cannot be removed.",
  "error.release_exists": "A release already exists for this.",
  "error.structure_incomplete": "This structure is missing a required entry.",
  "error.unit_not_allowed": "This unit is not allowed here.",
  "error.unknown_channel": "HOMEBASE does not know this contact channel.",
  "error.unknown_payment_kind": "HOMEBASE does not know this payment kind.",
  "error.unknown_payment_method": "HOMEBASE does not know this payment method.",
  "error.invalid_stage": "The payment stage given is not an identifier.",
  "error.no_payment_terms": "No payment plan applies to this order — there is no stage a payment could be assigned to.",
  "error.stage_not_in_plan": "This stage does not belong to the payment plan that applies to this order.",
  "error.unknown_unit": "HOMEBASE does not know this unit.",
  "error.unknown": "Unknown error.",
  "error.network": "Server not reachable.",
  "error.unauthenticated": "Sign-in required.",
  "error.forbidden": "You do not have permission for this action.",
  "error.invalid_credentials": "Sign-in failed.",
  "error.too_many_attempts": "Too many attempts. Please try again in {minutes} minutes.",
  "error.too_many_attempts_one": "Too many attempts. Please try again in one minute.",
  "error.bad_request": "The input is incomplete or invalid.",
  "error.not_found": "Not found.",
  "error.unknown_anchor_kind":
    "An anchor sits either on a milestone or on a payment.",
  "error.unknown_payment_anchor": "HOMEBASE does not know this payment anchor.",
  "error.anchor_ambiguous":
    "A lead time rule anchors on a milestone OR on a payment \u2013 not on both.",
  "error.china_name": "A China event needs a name.",
  "error.china_span":
    "A China event needs a start and an end; for a one-day festival, the same day twice.",
  "error.china_lead": "Lead-in and recovery are a number of days from zero.",
  "error.china_status": "A core date is either officially confirmed or preliminary.",
  "error.china_doppelt": "This event already exists with that start date.",
  "error.group_occupied":
    "This slot already holds a current document. Upload the file as a new version \u2013 "
    + "the previous one is kept as an earlier version.",
  "error.open_range_dimension": "A variant axis needs a definite value or a complete range. An open bound such as \u201Cfrom 85\u201D does not describe an execution.",
  "error.invalid_angle": "A beam angle needs a value. A second value describes an asymmetric optic (30\u00B0 \u00D7 70\u00B0) \u2014 a zoom range needs both bounds.",
  "error.article_in_use": "This article is already assigned to an execution of this product model.",
  "error.article_is_placeholder": "A placeholder is not a SKU. It cannot be assigned to an execution.",
  "error.article_not_assigned": "This article does not belong to any execution of this product model.",
  "error.confirmation_required": "This step has to be confirmed explicitly.",
  "error.dimension_in_use": "Executions still carry values on this variant axis. Adjust the executions first.",
  "error.duplicate_dimension": "This property is already a variant axis on this product model.",
  "error.family_mismatch": "Article and product model belong to different product families.",
  "error.focus_needs_article": "A focus can only be set on an execution that has an article.",
  "error.invalid_focus": "This focus value is not permitted.",
  "error.invalid_model": "A product model is required.",
  "error.invalid_provenance": "This provenance is not permitted.",
  "error.invalid_transition": "This transition is not possible from here.",
  "error.model_in_use": "This product model still has versions, articles, texts, properties, documents, packaging versions or information requests.",
  "error.value_in_use": "Executions still carry this value. Adjust the executions first.",
  "error.variant_exists": "This combination of axis values already exists on this product model.",
  "error.document_too_large": "The file is too large. 20 MB at most.",
  "error.document_type_unsupported": "This kind of file is not stored. Allowed are PDF, IES, LDT, TXT, ZIP, PNG and JPG.",
  "error.document_name_too_long": "The file name is too long.",
  "error.model_required": "This variant belongs to no product model. A document for every variant needs one.",
  "error.conflict": "The version has changed in the meantime. Please reload the view.",
  "error.method_not_allowed": "This action is not allowed here.",
  "error.internal_error": "Internal error.",
  "error.weak_password": "The password must be at least {n} characters long and must not start or end with a space.",
  "error.password_mismatch": "The two entries of the new password do not match.",
  "error.current_password_wrong": "The current password is not correct.",
  "error.invalid_role": "The role must be Administrator or Viewer.",
  "error.invalid_username": "Username: 3 to 32 characters, lower case letters, digits, dot, hyphen, underscore, starting with a letter.",
  "error.invalid_name": "A display name is required.",
  "error.username_taken": "This username is already taken.",
  "error.role_missing": "The role does not exist in the data.",
  "error.not_editable": "Service accounts are not managed here.",
  "error.self_demotion": "You cannot give up your own administrator role.",
  "error.self_deactivation": "You cannot deactivate your own account.",
  "error.last_admin": "At least one active administrator must remain.",
  "error.reserved_username": "This username belongs to the namespace of automated tests and is not allowed.",
  // ── Product families ───────────────────────────────────────────────────
  "nav.families": "Product families",
  "families.search": "Search",
  "families.searchPlaceholder": "Search product family …",
  "families.countLabel": "product families",
  "families.title": "Product families",
  "families.colFamilies": "Product families",
  "sl.categories": "Main categories",
  "sl.category": "Main category",
  "sl.family": "Product family",
  "sl.categoryDetailAria": "Main category – detail",
  "sl.title": "Lifecycle of the {art}",
  "sl.stateActive": "active",
  "sl.stateArchived": "archived",
  "sl.stateTrashed": "in the bin",
  "sl.stateFilter": "State",
  "sl.filterActive": "Active only",
  "sl.filterArchived": "Archived only",
  "sl.filterTrashed": "Bin only",
  "sl.filterAll": "All states",
  "sl.emptyTitle": "Nothing in this state",
  "sl.emptyHint": "Choose another state – archived and binned structures are kept.",
  "sl.noDependants": "Nothing depends on it – this structure can be deleted for good once it is in the bin.",
  "sl.dependants": "Depending on it: {list}.",
  "sl.name": "Name",
  "sl.code": "Code",
  "sl.codeFree": "Changeable while nothing depends on it.",
  "sl.codeLocked": "Locked: the code is the key for display names and import mappings. The name can be corrected at any time.",
  "sl.archive": "Archive",
  "sl.reactivate": "Reactivate",
  "sl.toTrash": "Move to bin",
  "sl.restore": "Restore",
  "sl.archiveExplains": "Archiving deletes nothing: existing models, articles and texts stay; the structure is simply no longer offered for new entries.",
  "sl.purgeOpen": "Delete for good …",
  "sl.purgeText": "The {art} {code} will be deleted for good. This cannot be undone; the log keeps code, name, person and time.",
  "sl.purgeLosing": "This also removes: {list}.",
  "sl.purgeConfirmLabel": "Type {code} to confirm",
  "sl.purge": "Delete for good",
  "families.emptyTitle": "No product family found",
  "families.emptyHint": "Adjust the search term or the category.",
  "families.colName": "Product family",
  "families.colCategory": "Product category",
  "families.colArticles": "Articles",
  "families.detailAria": "Product family detail",
  "families.code": "Identifier",
  "families.legacyAlias": "Historical designation",
  "families.articles": "Assigned articles",
  "families.noArticles": "No article is assigned to this product family.",
  "families.primarySplit": "({primary} primary, {secondary} additional relation)",
  "families.secondary": "additional relation",
  "families.secondaryHint": "This article belongs primarily to a different product family.",
  "texts.emptyFamily": "No texts have been created for this product family yet.",
  // ── Editorial product texts ────────────────────────────────────────────
  "texts.section": "Texts",
  "texts.loading": "Loading texts …",
  "texts.emptyAll": "No texts have been created for this article yet.",
  "texts.noLocaleLang": "No {language} version.",
  "texts.otherLangAvailable": "A {language} version exists.",
  "texts.formLanguage": "{language} version",
  "texts.details": "Details",
  "texts.sourceOwn": "own text of this SKU",
  "texts.sourceRule": "rule-generated",
  "texts.sourceRuleNote": "This line is not stored. HOMEBASE builds it from the product family's selection matrix and shows it in the product header.",
  "texts.sourceRuleAdopt": "When you create the text, this line is already in the field, ready to be kept or changed.",
  "texts.sourceRuleLang": "The rule builds this line in {language}. A version in the selected language starts from scratch.",
  "texts.unresolved": "Unresolved placeholders: {list}",
  "texts.unresolvedHint": "The inherited text block of the product model was filled with the values of this execution. These placeholders have no value on the article — they remain in place rather than being replaced by an invented figure.",
  "texts.statusAfterSave": "Review, release and reset become available again once the change has been saved or discarded.",
  "texts.inheritedFrom": "inherited from product family {name}",
  "texts.original": "Original",
  "texts.translatedFrom": "translated from {language}",
  "texts.sourceChanged": "The original has been changed since",
  "texts.writeOwn": "Write an own text for this SKU",
  "texts.releasedBy": "released by {name} on {date}",
  "texts.basisChanged": "Text basis changed",
  "texts.basisChangedHint": "The factual basis of this SKU has changed since release. The text itself was not altered \u2014 it should be reviewed.",
  "texts.markReview": "Flag for review",
  "texts.basisTitle": "Text basis of this SKU",
  "texts.basisNote": "What is established about exactly this execution. The basis for a product text \u2014 and later for text generation.",
  "texts.basisShow": "Show text basis",
  "texts.basisHide": "Hide text basis",
  "texts.basisCategory": "Main category",
  "texts.basisFamily": "Product family",
  "texts.basisModel": "Product model",
  "texts.basisNumber": "Article number",
  "texts.basisExecution": "Execution",
  "texts.basisProperties": "Properties of this SKU",
  "texts.basisEmptyExecution": "This SKU is not assigned to an execution of a product model.",
  "texts.basisEmptyProperties": "No properties are recorded for this SKU.",
  "texts.basisFingerprint": "Fingerprint",
  "texts.basisGenerateLater": "Text generation from this basis is prepared but not yet connected.",
  "texts.edit": "Edit",
  "texts.editAria": "Edit {type} in {language}",
  "texts.add": "Add version",
  "texts.addAria": "Add {type} in {language}",
  "texts.addFirst": "Create text",
  "texts.save": "Save",
  "texts.saving": "Saving …",
  "texts.remove": "Remove version",
  "texts.removeConfirm": "Really remove this language version? The text will no longer exist in this language.",
  "texts.origin": "Origin",
  "texts.method": "Creation",
  "texts.supplier": "Supplier",
  "texts.isOriginal": "This is the original version",
  "texts.charsUsed": "{used} of {max} characters",
  "texts.markReviewed": "Mark as reviewed",
  "texts.release": "Release",
  "texts.backToDraft": "Back to draft",
  "texts.editedResetsStatus": "An edited version returns to draft.",
  "textType.artikelname": "Article name",
  "textType.kurzbeschreibung": "Short description",
  "textType.langtext": "Long text",
  "textType.ausschreibungstext": "Tender text",
  "textType.anwendung": "Application",
  "textType.hinweis": "Note",
  "textStatus.entwurf": "Draft",
  "textStatus.geprueft": "Reviewed",
  "textStatus.freigegeben": "Released",
  "textStatus.pruefung_noetig": "Review required",
  "textOrigin.supplier": "Supplier",
  "textOrigin.intern": "created internally",
  "textOrigin.import": "imported",
  "textOrigin.extern": "external source",
  "textMethod.erfasst": "originally written",
  "textMethod.uebernommen": "adopted unchanged",
  "textMethod.uebersetzt_manuell": "translated manually",
  "textMethod.uebersetzt_ki": "translated by AI",
  "textMethod.erzeugt_ki": "AI-generated",
  "textMethod.erzeugt_ki_bearbeitet": "AI-generated, edited by a person",
  "textLang.de": "German",
  "textLang.en": "English",
  "error.empty_body": "An empty text is not a language version.",
  "error.body_too_long": "The text is too long for this text type.",
  "error.invalid_language": "Unknown language.",
  "error.invalid_text_type": "Unknown text type.",
  "error.invalid_subject": "Exactly one subject required: article or product family.",
  "error.level_not_allowed": "This text type is not kept at this level.",
  "error.first_must_be_original": "The first language version of a text is the original.",
  "error.text_exists": "A text of this type already exists for this subject.",
  "error.original_not_translated": "An original version cannot be a translation.",
  "error.invalid_origin": "Unknown origin.",
  "error.invalid_method": "Unknown creation method.",
  "error.invalid_status": "Unknown status.",
  "error.supplier_required": "A supplier is required for supplier origin.",
  "error.supplier_not_allowed": "A supplier belongs to supplier origin only.",
  "error.source_missing": "The referenced source version does not exist.",
  "error.source_in_use": "Translations refer to this version. It cannot be removed.",
  "error.review_required": "A version is reviewed first and released afterwards.",
  "error.never_released": "Only a version that was released once can be flagged for review.",
  // ── Controlled terminology (TERMINOLOGY_DE_EN.md) ──────────────────────
  //
  // British English throughout. One German term has exactly one approved
  // English display -- no alternating synonyms. Only values that DESCRIBE an
  // object appear here; names of objects stay language-neutral.
  "category.notlicht": "Emergency lighting",
  "category.komponenten": "Components",
  "family.Treiber": "Drivers",
  "family.LED-Notlichtbeleuchtung": "LED emergency lighting",
  "provenance.quelle": "Source",
  "provenance.abgeleitet": "Derived",
  "provenance.bestaetigt": "Confirmed",
  // ── Technical properties ───────────────────────────────────────────────
  "props.section": "Technical properties",
  "props.loading": "Loading properties …",
  "props.empty": "No technical properties have been recorded for this article yet.",
  "props.emptyFamily": "No properties have been recorded for this product family yet.",
  "props.add": "Add property",
  "sdb.countArticles": "articles",
  "sdb.countWithNeed": "of those with data needs",
  "sdb.countItems": "open entries",
  "sdb.countQuestions": "open questions",
  "sdb.hint": "What you see here is computed, not stored: missing information a supplier could generally answer. It is not yet a procurement decision and not yet a request.",
  "sdb.noArticles": "No product is assigned to this partner, so there is nothing to source.",
  "sdb.noNeed": "No information is missing on this supplier's products that they could answer.",
  "sdb.viewAria": "Choose view",
  "sdb.byQuestion": "By question",
  "sdb.byProduct": "By product",
  "sdb.oneQuestion": "1 open question",
  "sdb.nQuestions": "{n} open questions",
  "sdb.selectAll": "Select all",
  "sdb.selectNone": "Clear selection",
  "sdb.selected": "{n} selected",
  "sdb.createOutput": "Create output",
  "sdb.outputTitle": "Output for the supplier",
  "sdb.outputHint": "English, because the text goes to the supplier. It is built from the current selection and stored nowhere — it is not a request and not a process.",
  "sdb.copy": "Copy to clipboard",
  "sdb.copied": "copied",
  "sdb.supplierItemNo": "Supplier item no. {nr}",
  "sdb.recommended": "recommended",
  "sdb.oneArticle": "1 article",
  "sdb.nArticles": "{n} articles",
  "reqGroup.grundriss": "Footprint (length and width, or diameter)",
  "reqPurpose.stammdaten": "Master data",
  "reqPurpose.text": "Text",
  "reqPurpose.compliance": "Compliance",
  "reqPurpose.kalkulation": "Costing",
  "reqPurpose.logistik": "Logistics",
  "req.supplierObtainable": "obtainable from the supplier",
  "req.missing": "missing",
  "req.requested": "requested",
  "req.notApplicable": "not applicable",
  "req.derived": "derived from luminous flux and power",
  "req.add": "Add information",
  "props.edit": "Edit",
  "props.editAria": "Edit {name}",
  "props.confirmAria": "Confirm {name}",
  "props.unconfirmAria": "Withdraw confirmation for {name}",
  "props.remove": "Remove",
  "props.removeConfirm": "Really remove this property value?",
  "props.save": "Save",
  "props.saving": "Saving …",
  "props.confirm": "Confirm",
  "props.unconfirm": "Withdraw confirmation",
  "props.confirmHint": "Changing a value withdraws its confirmation.",
  "props.value": "Value",
  "props.valueFrom": "from",
  "props.valueTo": "to",
  "props.origin": "Origin",
  "props.method": "Creation",
  "props.findingModel": "Model deviation",
  "props.findingConflict": "Conflict with second source",
  "props.findingDte": "DTE to be updated",
  "props.proposalCurrent": "Current",
  "props.proposalNew": "New reference",
  "props.proposalAccept": "Adopt new reference",
  "props.proposalReject": "Keep current value",
  "props.proposalAlready": "This value already applies — the proposal is moot.",
  "props.proposalHint": "The decision is recorded with person and time. Confirming applies to the existing value and is a different act.",
  "props.proposalPending": "The existing value stays unchanged until someone decides. A decision path in the interface is still missing.",
  "stock.title": "Stock",
  "opz.aria": "Operational status",
  "split.label": "Width of the product list",
  "split.hint": "Drag to resize · double-click to reset",
  "opz.offers": "Quotes 12M",
  "opz.offersHalfYear": "of which in the last 6 months: {n}",
  "opz.ordered": "On order",
  "opz.eta": "ETA LG",
  "opz.undetermined": "not determined",
  "stock.pieces": "pcs",
  "stock.asOf": "As of",
  "stock.source": "Source",
  "stockSource.dte-lager": "DTE stock report",
  "overview.parts": "Components",
  "overview.component": "Component used",
  "overview.breaker": "Fixtures per breaker {characteristic}{current} A",
  "overview.breakerUnit": "fixtures",
  "props.sourceSegment": "Source text",
  "props.chooseProperty": "Property",
  "props.chooseValue": "Select value",
  "props.yes": "yes",
  "props.no": "no",
  "props.familyNote": "Properties of the product family. They apply to the family and are not carried over to articles.",
  "propGroup.lichttechnik": "Lighting",
  "propGroup.elektrik": "Electrical / Control",
  "propGroup.schutz": "Protection / Quality",
  "propGroup.konformitaet": "Certifications, Conformities & Evidence",
  "propGroup.mechanik": "Mechanics / Dimensions",
  "propGroup.optik": "Optics / Functions",
  "propGroup.identitaet": "Identity / Origin",
  "prop.power": "Power",
  "prop.luminous_flux": "Luminous flux",
  "prop.efficacy": "Luminous efficacy",
  "prop.cct": "Correlated Colour Temperature (CCT)",
  "prop.light_colour": "Light colour",
  "prop.cct_tunable": "Tunable white",
  "prop.cri": "Colour Rendering Index (CRI)",
  "prop.sdcm": "Standard Deviation of Colour Matching (SDCM)",
  "prop.ugr": "Glare / UGR",
  "prop.control_gear": "Control",
  "prop.dimmable": "Dimmable",
  "prop.ip_rating": "IP rating",
  "prop.ik_rating": "IK rating",
  "prop.certification": "Certifications, Conformities & Evidence",
  "prop.length": "Length",
  "prop.width": "Width",
  "prop.height": "Height",
  "prop.diameter": "Diameter",
  "prop.cutout_size": "Cut-out size",
  "prop.weight": "Product weight",
  "prop.housing_material": "Housing material",
  "prop.housing_colour": "Housing colour",
  "prop.beam_angle": "Beam angle",
  "prop.sensor": "Sensor",
  "prop.emergency_duration": "Emergency duration",
  "propValue.nw": "Neutral white",
  "propValue.ww": "Warm white",
  "propValue.tl": "Daylight",
  "propValue.ww+": "ww+",
  "propValue.tl+": "tl+",
  "propValue.IP40": "IP40",
  "propValue.IP65": "IP65",
  "propValue.IP66": "IP66",
  "propValue.IP69K": "IP69K",
  "propValue.IK08": "IK08",
  "propValue.IK09": "IK09",
  "propValue.IK10": "IK10",
  "propValue.on_off": "On/Off",
  "propValue.triac": "TRIAC",
  "propValue.analog_1_10v": "1-10 V",
  "propValue.dali": "DALI",
  "propValue.dali_2": "DALI 2",
  "propValue.dali_push": "DALI Push",
  "propValue.atex": "ATEX",
  "propValue.ifs": "IFS",
  "propValue.ce_lvd": "CE (LVD)",
  "propValue.ce_emc": "CE (EMC)",
  "propValue.rohs": "RoHS",
  "propValue.tuev": "TÜV",
  "propValue.enec": "ENEC",
  "propValue.d_mark": "D-MARK",
  "propValue.nsf": "NSF",
  "propValue.haccp": "HACCP",
  "propValue.sosen": "SOSEN",
  "propValue.lifud": "LIFUD",
  "propValue.boke": "BOKE",
  "propValue.kgp": "KGP",
  "propValue.eaglerise": "EAGLERISE",
  "propValue.polycarbonat": "Polycarbonate",
  "propValue.aluminium": "Aluminium",
  "propValue.aluminium_druckguss": "Die-cast aluminium",
  "propValue.weiss": "white",
  "propValue.ral9016": "white (RAL 9016)",
  "propValue.schwarz": "black",
  "propValue.silber": "silver",
  "propValue.daylight": "Daylight sensor",
  "propValue.motion": "Motion sensor",
  "propMethod.erfasst": "recorded",
  "propMethod.uebernommen": "adopted unchanged",
  "propMethod.extrahiert": "extracted from free text",
  "propMethod.abgeleitet": "derived",
  "error.invalid_definition": "Unknown property.",
  "error.invalid_number": "Please enter a number only — without a unit.",
  "error.invalid_boolean": "This property requires a true/false value.",
  "error.invalid_enum": "This value does not belong to the property's value set.",
  "error.invalid_range": "The lower bound is above the upper bound.",
  "error.invalid_value": "The value does not match the property's value type.",
  "error.invalid_quality": "Unknown data quality status.",
  "error.out_of_range": "The value is outside the permitted range.",
  "error.not_multi": "This property carries only one value.",
  "error.value_exists": "A value for this property already exists for this subject.",
  "error.empty_value": "An empty value is not a value.",
  "error.share_sum": "The shares of a payment plan add up to 100%.",
  "error.invalid_share": "A share is above 0 and at most 100%.",
  "error.unknown_event": "This payment event is not in the catalogue.",
  "error.invalid_offset": "A term is a whole number of days from 0.",
  "error.offset_required": "This payment event needs a term in days.",
  "error.offset_not_allowed": "This payment event has no term.",
  "error.invalid_rating": "A rating is between 1.0 and 5.0 in half steps.",
  "error.asymmetric_beam_angle": "An asymmetric beam angle is not a range and is not reinterpreted.",
  // ── Labels of technical codes ──────────────────────────────────────────
  // These codes are stored in the database. The DISPLAY is translated,
  // the stored value stays untouched.
  "lifecycle.aktiv": "Active",
  "lifecycle.auslaufend": "Phasing out",
  "lifecycle.inaktiv": "Inactive",
  "lifecycle.nicht_freigegeben": "Not released",
  "quality.belastbar": "reliable",
  "quality.warnung": "warning",
  "quality.pruefung_erforderlich": "review required",
  "articleRole.hauptprodukt": "Main product",
  "articleRole.komponente": "Component",
  "articleRole.zubehoer": "Accessory",
  "articleRole.ersatzteil": "Spare part",
  "articleRole.option": "Option",
  "flag.erststatus_zu_pruefen": "Initial status to be reviewed",
  "flag.produktfamilie_zu_pruefen": "Product family to be reviewed",
  "flag.rolle_zu_pruefen": "Article role to be reviewed",
  "flag.zollsatz_zu_pruefen": "Customs rate to be reviewed",
  "flag.verpackungsdaten_fehlen": "Packaging data missing",
  "flag.artikelnummer_konflikt": "Article number conflict",
  "flag.supplier_schreibweise": "Supplier spelling",
  "flag.fracht_ohne_volumen": "Freight without volume",
  "flag.platzhalter_zu_pruefen": "Placeholder to be reviewed",
  // ── Product model and variant level ────────────────────────────────────
  "nav.models": "Product models",
  "models.loading": "Loading product models \u2026",
  "models.search": "Search",
  "models.searchPlaceholder": "Search model code, model name or product family \u2026",
  "models.countLabel": "product models",
  "models.emptyTitle": "No product models yet",
  "models.emptyHint": "The product model level is available but not enforced. An article may exist without a product model.",
  "models.create": "Create product model",
  "models.detailAria": "Product model detail",
  "models.fallbackTitle": "Product model",
  "models.logistics": "Logistics & customs",
  "models.logisticsNote": "Applies to every variant of this model that has no value of its own. A value on the SKU takes precedence.",
  "models.editTariff": "Edit tariff code",
  "models.tariffHint": "Digits only, leading zeros are kept. Leaving it empty removes the code from the model; SKUs with their own code stay unchanged.",
  "models.code": "Model code",
  "models.name": "Model name",
  "models.colModel": "Product model",
  "models.colFamily": "Product family",
  "models.colVariants": "Versions",
  "models.colMaterialised": "SKUs",
  "models.confirm": "Confirm",
  "models.unconfirmed": "not confirmed",
  "models.unconfirmedTitle": "Rule-based proposal, not yet confirmed.",
  "models.choose": "please choose",
  "models.dimensions": "Version properties",
  "models.dimensionsNote": "Properties by which the offered versions differ. Values that follow from another property – such as efficacy per CRI – do not belong here.",
  "models.dimensionProperty": "Property of the axis",
  "models.noDimensions": "No version properties have been defined for this product model yet.",
  "models.addDimension": "Add version property",
  "models.removeDimension": "Remove property",
  "models.removeDimensionConfirm": "Really remove this version property?",
  "models.noValues": "No allowed value has been defined for this property yet.",
  "models.addValue": "Add permitted value",
  "models.removeValue": "Remove value",
  "models.removeValueConfirm": "Really remove this permitted value?",
  "models.valueVariantsTitle": "{count} versions carry this value",
  "models.valueNumber": "Value",
  "models.valueMin": "from",
  "models.valueMax": "to",
  "models.valueAngleX": "Beam angle X",
  "models.valueAngleY": "Beam angle Y (optional)",
  "models.valueCode": "Value",
  "models.valueText": "Value",
  "models.variants": "Versions",
  "models.variantsCount": "versions",
  "models.materialised": "with article number",
  "models.virtual": "without article number",
  "models.noVariants": "No version has been created for this product model yet.",
  "models.addVariant": "Add version",
  "models.editVariant": "Edit version",
  "models.removeVariant": "Remove version",
  "models.removeVariantConfirm": "Really remove this version? An assigned article remains.",
  "models.colVariant": "Version",
  "models.colFocus": "Focus",
  "models.colPrice": "List price",
  "models.colAvailability": "Availability",
  "models.priceValidFrom": "released, valid from {date}",
  "models.colDte": "DTE",
  "models.colActions": "Actions",
  "models.virtualLabel": "Version without article number",
  "models.virtualTitle": "This version is intended but has no article record yet. This is not an error.",
  "models.noDteTitle": "Without an article record there is no DTE status \u2014 not \u201cnot listed\u201d.",
  "models.focusRank": "Focus {rank}",
  "models.focusTitle": "Focus variant. Focus is a deliberate commercial prioritisation and never arises automatically.",
  "models.focus": "Focus rank",
  "models.focusPlaceholder": "empty = no focus",
  "models.focusNote": "Focus is set and never derived from stock, revenue, order history or DTE status. It requires a materialised SKU.",
  "models.sku": "SKU",
  "models.notMaterialised": "without article number",
  "models.variantLabel": "Label",
  "models.placeholders": "Placeholder records",
  "models.placeholderBadge": "Placeholder",
  "models.placeholderTitle": "The article number contains an unresolved dimension value. Not a fully specified SKU.",
  "models.placeholderNote": "These records belong to the product model but are not fully specified SKUs. They are neither kept as variants nor listed in DTE.",
  "models.otherArticles": "Accessories & components",
  "models.articleSection": "Product model and system relationship",
  "models.model": "Product model",
  "models.noModel": "no product model",
  "models.assign": "Assign",
  "models.reassign": "Change",
  "models.noModelsInFamily": "No product model exists yet in the product families of this article.",
  "models.variant": "Variant",
  "models.noVariantValues": "no axis values defined",
  "dte.label": "DTE listing",
  "dte.change": "Change status",
  "dte.note": "Note",
  "dte.notePlaceholder": "Note (required on failure)",
  "dte.since": "since {date}",
  "dte.delistConfirm": "Really delist this SKU? Delisting is only permitted while DTE holds neither stock nor movement.",
  "dte.notListedNote": "Not listed in DTE means: no stock figure available. It does not mean stock 0.",
  "dte.nicht_gelistet": "not listed",
  "dte.vorgemerkt": "scheduled",
  "dte.gelistet": "listed",
  "dte.fehlgeschlagen": "failed",
  "props.sectionModel": "Model properties",
  "props.modelNote": "A model property applies to all variants of this model. It is not copied onto the SKUs \u2014 a SKU displays it, it does not store it.",
  "props.emptyModel": "No model properties recorded for this product model yet.",
  "texts.sectionModel": "Model-wide text blocks",
  "texts.noteModel": "These texts describe the series, not a single execution. They apply only if the statement holds for ALL executions of this product model. The product text of a SKU is written on the SKU.",
  "texts.emptyModel": "No model-wide text blocks recorded for this product model yet.",
  "texts.inheritedFromModel": "inherited from product model {name}",
  "common.yes": "yes",
  "common.no": "no",
  "common.edit": "Edit",
  "common.remove": "Remove",
  "common.save": "Save",
  // ── Property review queue ─────────────────────────────────────────────────
  "nav.cleanup": "Cleanup",
  "cleanup.title": "Article master cleanup",
  "cleanup.lead": "This view shows where the article master still needs work — family by family, instead of opening 357 records one at a time. Pick a family, pick a model, review the real articles. Nothing is confirmed here: deciding a value stays in the product file.",
  "cleanup.sumRow": "All families",
  "cleanup.sumHint": "Totals across all product families — computed from the rows of this table, not a second source.",
  "cleanup.chooseModel": "Choose a model",
  "cleanup.noModels": "This family carries no product model yet.",
  "cleanup.noFamilies": "No product family with articles.",
  "cleanup.colFamily": "Product family",
  "cleanup.colModels": "Models",
  "cleanup.colArticles": "Articles",
  "cleanup.colAssigned": "assigned",
  "cleanup.colUnassigned": "no model",
  "cleanup.colConflicts": "Conflicts",
  "cleanup.colUnconfirmed": "unconfirmed",
  "cleanup.colFlags": "Flags",
  "cleanup.conflictsConfirmedHint": "{n} of them confirmed as reliable while the conflict stood open.",
  "cleanup.skus": "SKUs of this model ({n})",
  "cleanup.noSkus": "No article is assigned to this model yet.",
  "cleanup.unassigned": "Articles of the same family without a model ({n})",
  "cleanup.unassignedHint": "These articles belong to the same product family and carry no model yet. This is NOT a suggested assignment — a similar article number is not evidence. Assignment happens deliberately in the product file.",
  "cleanup.noUnassigned": "Every article of this family is assigned to a model.",
  "cleanup.colArticle": "Article",
  "cleanup.colDescription": "Description (HOMEBASE)",
  "cleanup.colProps": "Properties",
  "cleanup.colConflict": "Conflict with the source",
  "cleanup.colPrice": "List price",
  "cleanup.colStock": "Availability / stock",
  "cleanup.colSupplier": "Supplier",
  "cleanup.colOpen": "open",
  "cleanup.propsHint": "confirmed / warning / review required",
  "cleanup.focus": "Focus {n}",
  "cleanup.confirmedDespite": "confirmed despite conflict",
  "cleanup.confirmedDespiteHint": "The value was set to reliable by hand while this conflict stood open. That is a decision, not a fault — but it deserves a second look.",
  "cleanup.priceValidFrom": "released, valid from {date}",
  "cleanup.asOf": "as of {date}",
  "cleanup.asOfHint": "Source {source}. The reference date is the source file's date, not the import time.",
  "cleanup.proposals": "{n} open value proposals",
  "cleanup.infoRequests": "{n} open information requests",
  "cleanup.markDte": "DTE needs correcting",
  "cleanup.resolve": "done",
  "cleanup.dteNoteLabel": "What is wrong or outdated in DTE?",
  "cleanup.dteNotePlaceholder": "e.g. DTE carries 34,000 lm, correct is 32,000 lm.",
  "cleanup.dteSave": "Record the flag",
  "cleanup.cancel": "Cancel",
  "cleanup.noDteWrite": "HOMEBASE writes nothing to DTE. The flag is recorded here; the correction in DTE is made by hand.",
  "cleanup.dteScope": "What can be compared against DTE",
  "cleanup.comparable": "comparable",
  "cleanup.notComparable": "not comparable — HOMEBASE stores no DTE value for this",
  "cleanup.field.merkmale": "Technical properties",
  "cleanup.field.bestand": "Stock",
  "cleanup.field.bezeichnung": "Description",
  "cleanup.field.preis": "Price",
  "cleanup.field.lieferant": "Supplier",
  "texts.draft": "Generate draft",
  "texts.draftTitle": "Builds a draft from the product family's selection matrix or the product model's text block. The draft is not saved — it appears in the input field.",
  "texts.draftMissing": "The draft is incomplete. These values are missing on the article: {list}",
  "texts.draftReady": "Draft built entirely from the structured values. Nothing was added or phrased.",
  "textMethod.erzeugt_regel": "built from a rule",
  "cleanup.colClassified": "of those classified",
  "cleanup.classifiedHint": "Accessory, spare part or component — correctly classified without a product model, not a maintenance backlog.",
  "cleanup.roleNone": "no role",
  "tk.driverBrand": "Driver brand",
  "tk.driverModel": "Driver model",
  "tk.componentOpen": "Open {number}",
  "tk.evidenceOpen": "not yet confirmed",
  "tk.evidenceOpenHint": "The evidence is recorded but not yet professionally confirmed. Only then may a text rely on it.",
  "tk.evidenceFromDocuments": "The property follows from its evidence (standard) -- maintained under Documents.",
  "tk.evidencePartial": "{ok} of {total} evidence records changed. Error: {error}",
  "capability.ballwurfsicher": "Ball impact resistant",
  "prop.surge_line_earth": "Surge protection line-earth",
  "prop.surge_line_line": "Surge protection line-line",
  "nav.review": "Property review",
  "review.title": "Product property review",
  "review.lead": "Every machine-derived value appears here with its source wording and the finding of the evaluation. A finding is a technical observation — a value is only confirmed by a person.",
  "review.loading": "Loading property values …",
  "review.values": "property values",
  "review.search": "Search",
  "review.searchPlaceholder": "Article number or source wording …",
  "review.findingClass": "Finding class",
  "review.allClasses": "All finding classes",
  "review.property": "Property",
  "review.allProperties": "All properties",
  "review.quality": "Quality status",
  "review.allQualities": "All quality statuses",
  "review.role": "Article role",
  "review.allRoles": "All article roles",
  "review.confirm": "Confirm",
  "review.defer": "Defer",
  "review.edit": "Edit in article",
  "review.openArticle": "Open article",
  "review.emptyTitle": "No property values",
  "review.emptyHint": "No value matches this selection. Resetting the filters shows all of them again.",
  "review.progressAria": "{done} of {total} values confirmed",
  "review.runInfo": "Findings from the evaluation run · source checksum {checksum} · second source {second}",
  "review.noSecondSource": "none",
  "review.runStale": "The short description has changed since the evaluation — the findings are out of date.",
  "review.noRun": "No evaluation run yet. Without one, no value carries a finding.",
  "reviewClass.A": "confirmed — second source states the same",
  "reviewClass.B": "value correct, source only indirect",
  "reviewClass.C": "correctable",
  "reviewClass.D": "contradictory",
  "reviewClass.E": "not representable in the model",
  "reviewClass.F": "error in the source",
  "finding.dte_bestaetigt": "second source confirms",
  "finding.keine_zweitquelle": "no second source",
  "finding.zweitquelle_schweigt": "second source silent",
  "finding.zweitquelle_ungenauer": "second source less precise",
  "finding.nicht_maschinell_nachpruefbar": "not machine-verifiable",
  "finding.quelle_nennt_weitere_lichtfarben": "source names further light colours",
  "finding.abweichung_erstquelle": "differs from the source wording",
  "finding.dte_konflikt": "source conflict",
  "finding.winkel_asymmetrisch": "asymmetric beam angle",
  "finding.cri_schwelle": "CRI given as a threshold",
  "finding.leistung_geteilt": "split power",
  "finding.leistung_stufen": "switchable power steps",
  "finding.ausschnitt_zweiachsig": "two-axis cut-out size",
  "finding.dte_nicht_abbildbar": "second source not representable",
  "finding.flaecheneinheit_bei_laenge": "area unit for a length",
  "finding.einheit_doppelt": "unit written twice",
  "finding.kelvin_mit_schranke": "colour temperature with an upper bound",
  // ── Logistics: supplier-product relation and packaging ──────────────────
  //
  // Working terminology from the „Logistik & Beschaffung V1" assignment. The
  // final DE/EN wording for this area is not yet anchored in
  // TERMINOLOGY_DE_EN.md -- see open point.
  "log.inheritedFrom": "inherited from {model}",
  "log.tariffInheritHint": "The code comes from product model {model}. A code entered here applies to this SKU only; leave it empty to keep the model's.",
  "log.packagingInheritHint": "This SKU has no packaging of its own; the one of product model {model} applies. Packaging recorded here replaces it for this SKU. The model's packaging is maintained on the product model.",
  "log.tariffCode": "Tariff code",
  "log.packagingDetails": "Packaging revisions in detail",
  "log.undetermined": "not determined",
  "log.notApplicable": "n/a",
  "log.lengthOrDiameter": "Length / Ø",
  "log.heightShort": "Height",
  "log.productWeight": "Product weight",
  "log.groupPackagingAct": "Packaging Act",
  "log.materialShare": "Material share",
  "log.tariffPlaceholder": "e.g. 94054200",
  "log.tariffHint": "A code, not a number. Leading zeros are kept. No automatic classification.",
  "log.noRelations": "No supplier relation is recorded for this article.",
  "log.addRelation": "Add supplier relation",
  "log.removeRelation": "Release relation",
  "log.supplier": "Supplier",
  "log.chooseSupplier": "Choose supplier …",
  "log.supplierArticleNumber": "Supplier article number",
  "log.moq": "MOQ",
  "log.orderMultiple": "Order multiple",
  "log.leadTime": "Lead time",
  "log.dutyRate": "Duty rate",
  "log.since": "since {date}",
  "log.days": "{n} days",
  "log.confirm": "Confirm as reliable",
  "log.origin": "Origin",
  "log.method": "How it came about",
  "log.provenance": "Origin and method",
  "log.noPackaging": "No packaging revision is recorded for this relation yet.",
  "log.addVersion": "Record new packaging",
  "log.removeVersion": "Withdraw current revision",
  "log.revision": "Revision {n}",
  "log.current": "current",
  "log.historic": "historical",
  "log.showHistory": "Show {n} earlier revisions",
  "log.hideHistory": "Hide earlier revisions",
  "log.changeReason": "Reason for change",
  "log.changeNote": "Note on the change",
  "log.provider": "Packaging provided by",
  "log.providerUnknown": "not determined",
  "log.validFrom": "Valid from",
  "log.datePlaceholder": "YYYY-MM-DD",
  "log.productDims": "Product dimensions of this revision",
  "tech.coreLegend": "Highlighted: the core properties of this category.",
  "log.levelProduct": "Product",
  "log.innerMaterials": "Inner box material",
  "log.masterMaterials": "Master carton material",
  "log.gramShort": "g",
  "log.dimsFromRevision": "(as reported by the revision)",
  "log.weightBalance": "Weight balance",
  "log.computed": "computed",
  "log.levelClear": "Clear values",
  "log.levelReset": "Discard edits",
  "log.packagingWeight": "Packaging weight",
  "log.residualWeight": "of which unassigned",
  "log.perProduct": "per product",
  "log.balanceContent": "content ({n} pcs)",
  "log.balanceContentLevel": "content ({n} × {level})",
  "log.materialPerUnit": "Material per unit",
  "log.materialRecorded": "recorded",
  "log.materialDerived": "derived from the weight balance",
  "log.materialFrom": "{level}, {n} products per pack",
  "log.materialMissing": "No material weight is recorded for the {level} level yet — nothing can be derived per unit. The unassigned remainder of the weight balance is not a material weight.",
  "log.balanceImpossible": "— this gross weight cannot be right",
  "log.balancePackagingWeight": "packaging weight",
  "log.balanceMaterialSum": "material total",
  "log.balanceDifference": "difference",
  "log.balanceMatches": "matches",
  "log.balanceMismatchHint": "The material total does not yet match the packaging weight. Please correct the material values or repeat the measurement.",
  "log.residualHistoric": "previously assigned: {material}",
  "log.confirmBlockedBalance": "Only confirmable once every level adds up exactly — see the difference in the weight balance.",
  "log.productDimsDeviation": "Deviating assumption of this revision",
  "log.newRevisionHint": "The revision number is assigned by the server. A new revision replaces no earlier one.",
  "log.cbmComputedHint": "CBM is calculated from the outer dimensions and is not entered.",
  "log.noLevels": "No packaging level is recorded for this revision.",
  "log.dimLength": "Length / Ø",
  "log.dimWidth": "Width",
  "log.dimHeight": "Height",
  "log.unitCount": "Units",
  "log.grossWeight": "Gross weight",
  "log.netWeight": "Net weight",
  "log.netWeightHint": "product without packaging",
  "log.editAria": "Edit logistics",
  "log.editing": "Edit logistics",
  "log.editHint": "Typed dimensions apply only once saved. Volume and volume per unit are calculated and stay as they are.",
  "log.save": "Save changes",
  "log.materialWeight": "Weight in grams",
  "log.groupPackaging": "Packaging",
  "log.groupCustoms": "Customs",
  "cust.articleDefault": "article value",
  "cust.originCountry": "Country of origin",
  "cust.originCountryPlaceholder": "CN",
  "cust.netWeight": "Net weight",
  "cust.netWeightCustoms": "Net weight for customs",
  "cust.fromProductNet": "taken from the product net weight",
  "cust.supplierStated": "{name} – supplier statement",
  "cust.recordExplicit": "Record an explicit customs value",
  "cust.netWeightPlaceholder": "kg",
  "cust.quality": "Reliability",
  "cust.confirm": "confirmed",
  "cust.note": "Note",
  "cust.notePlaceholder": "Source of the figure",
  "cust.missing": "missing",
  "cust.fromArticle": "from the article",
  "cust.fromRelation": "from this supplier relation",
  "cust.customsTruth": "valid for customs",
  "cust.notCustomsTruth": "not yet reliably confirmed",
  "cust.record": "Record",
  "cust.addOverride": "Record a deviation",
  "cust.hint": "Neither the supplier country nor the calculation weight is adopted. Only reliably confirmed figures count for customs.",
  "bew.title": "Supplier Rating",
  "bew.production": "Production Quality",
  "bew.reliability": "Reliability",
  "bew.communication": "Communication",
  "bew.price": "Price-Performance Ratio",
  "bew.paymentTerms": "Payment Terms",
  "bew.overall": "Overall Rating",
  "bew.overallFrom": "Ø across {n} criteria",
  "bew.overallNone": "no criterion rated yet",
  "bew.notRated": "Not Rated",
  "bew.clear": "clear",
  "bew.ratedBy": "Last rated by {wer} on {wann}.",
  "ek.groupProduct": "Product",
  "ek.groupPricing": "Price & Terms",
  "ek.incoterm": "Incoterm {term}",
  "ek.validSince": "valid since {date}",
  "ek.supplier": "Supplier",
  "ek.supplierArtNo": "Supplier art. no.",
  "ek.ratingTitle": "Supplier rating (average) -- maintained in the supplier portal",
  "ek.openSupplierRating": "Open {name} in the supplier portal -- with rating",
  "ek.price": "Purchase price",
  "ek.exw": "EXW",
  "ek.paymentTerms": "Payment terms",
  "ek.termsFromSupplier": "Supplier standard",
  "ek.termsFromArticle": "product-specific agreement",
  "ek.termsAfterDays": "{n} days",
  "ek.termsEditingSupplier": "Applies as this supplier's standard.",
  "ek.termsEditingArticle": "Applies to this article only.",
  "ek.termsOnlyThis": "this article only",
  "ek.termsDropArticle": "Withdraw deviation",
  "ek.termsSupplierWide": "Changes apply to every article of this supplier.",
  "ek.stageAdd": "Add stage",
  "ek.stageShare": "Share in percent",
  "ek.stageEvent": "Payment event",
  "ek.stageOffset": "Days",
  "ek.stageNote": "Addition",
  "ek.stageRemove": "Remove stage",
  "ek.stageSum": "Total {n} %",
  "payEvent.deposit": "Deposit",
  "ek.termsOnOrder": "upon order placement",
  "payEvent.after_qc": "after QC",
  "payEvent.before_bl": "before B/L",
  "payEvent.on_shipment": "on shipment",
  "payEvent.on_arrival": "on arrival",
  "payEvent.after_invoice": "after invoice",
  "payMethod.bank_transfer": "Bank transfer",
  "ord.orderable": "Orderable",
  "ord.blocked": "Not orderable",
  "orderBlock.number_placeholder": "placeholder number – no article number yet",
  "orderBlock.no_model_link": "no product model assigned",
  "orderBlock.variant_incomplete": "variant not fully determined",
  "orderBlock.lifecycle_blocks": "lifecycle status does not permit ordering",
  "orderBlock.no_supplier_link": "no supplier relation",
  "payMethod.letter_of_credit": "Letter of credit",
  "payMethod.paypal": "PayPal",
  "payMethod.credit_card": "Credit card",
  "payMethod.cash": "Cash",
  "payMethod.other": "other method",
  "ek.stageLabel": "Label",
  "ek.stageMethod": "Payment method",
  "ek.stageMethodNone": "not agreed",
  "fin.stages": "Actual payments",
  "fin.stageDue": "Due",
  "fin.stageShare": "Share",
  "fin.stageStatus": "Status",
  "fin.stagePartly": "partly",
  "fin.stageUnknown": "cannot be determined",
  "fin.stagePaid": "paid",
  "fin.stageRest": "remaining",
  "fin.stageSettled": "settled",
  "fin.stageOpen": "open",
  "fin.stageNoDue": "no due amount without an order value",
  "fin.stageAssign": "Assign stage",
  "fin.stageNone": "not assigned to a stage",
  "fin.unassigned": "Payments without a stage",
  "fin.unassignedHint": "They count towards the total. Their stage is not guessed.",
  "fin.methodDiffers": "differs from what was agreed",
  "payMethod.none": "not recorded",
  "ek.editAria": "Edit purchasing",
  "ek.editHint": "Typed entries apply only once saved. A new purchase price is recorded via “Record purchase price”.",
  "log.dimDiameter": "Diameter",
  "log.productDimsHint": "Product dimensions and weight are property values. They are edited in the Technical section.",
  "unit.gram": "g",
  "unit.ampere": "A",
  "log.errDimension": "{feld}: please enter a dimension greater than zero.",
  "log.errWeight": "Gross weight: please enter a weight greater than zero or leave the field empty.",
  "log.errUnitCount": "Units per package: please enter a whole number of at least 1.",
  "log.errMaterial": "Material weight: please enter a number greater than zero or leave the field empty.",
  "log.cbm": "CBM",
  "log.cbmPerUnit": "CBM per unit",
  "log.master": "Master carton",
  "log.innerOptional": "Inner box (optional)",
  "log.lengthMm": "Length mm",
  "log.widthMm": "Width mm",
  "log.heightMm": "Height mm",
  "log.innerLength": "Inner box length",
  "log.innerWidth": "Inner box width",
  "log.innerHeight": "Inner box height",
  "log.innerUnits": "Inner box units",
  "log.innerWeight": "Inner box gross weight",
  "log.masterLength": "Master carton length",
  "log.masterWidth": "Master carton width",
  "log.masterHeight": "Master carton height",
  "log.masterUnits": "Master carton units",
  "log.masterWeight": "Master carton gross weight",
  "log.addTest": "Add test record",
  "log.testType": "Test type",
  "log.testResult": "Result",
  "log.performedOn": "Performed on",
  "log.testStandard": "Standard",
  "log.testReference": "Reference",
  "log.testReferencePlaceholder": "e.g. Drop Test Report 2026-08",
  "log.testNote": "Note",
  "packLevel.inner": "Inner carton",
  "packLevel.master": "Master carton",
  "packLevel.pallet": "Pallet",
  "packReason.initial": "Initial record",
  "packReason.product_change": "Product change",
  "packReason.complaint": "Complaint",
  "packReason.optimization": "Optimisation",
  "packReason.supplier_change": "Supplier change",
  "packReason.other": "Other",
  "packProvider.supplier": "Supplier",
  "packProvider.greenius": "greenius",
  "packProvider.mixed": "mixed",
  "packMaterial.cardboard": "Cardboard",
  "packMaterial.plastic": "Plastic",
  "packMaterial.wood": "Wood",
  "packMaterial.other": "Other",
  "packTestType.drop_test": "Drop test",
  "packResult.passed": "passed",
  "packResult.failed": "not passed",
  "packResult.open": "open",
  "numberKind.aktuell": "current",
  "numberKind.historisch": "historical",
  "numberKind.alternativ": "alternative",
  // ── Article work page: recognise, understand, edit ────────────────────────
  "head.kind": "Article (SKU)",
  "head.sourceLabel": "Source designation from the import",
  "attn.aria": "Action required",
  "attn.show": "Show",
  "attn.review_flag_open": "open review flags",
  "attn.property_conflict": "conflicting properties",
  "attn.property_model_limit": "properties outside the model",
  "attn.core_property_missing": "missing core entries",
  "attn.requirement_missing": "expected information missing",
  "attnItem.tariff_code": "Tariff code",
  "attnItem.moq": "Minimum order quantity",
  "otexts.aria": "Descriptions",
  "otexts.title": "Description",
  "otexts.langAria": "Content language",
  "otexts.more": "read more",
  "otexts.less": "less",
  "head.priceReleased": "released from {date}",
  "head.priceCalculated": "calculated · not released",
  "head.priceNone": "not calculated",
  "head.listPrice": "List price, net",
  "head.imageNone": "No product image",
  "head.imageAlt": "Product family catalogue image",
  "tech.aria": "Technical overview",
  "tech.title": "Technical data",
  "propColumn.licht": "Light",
  "propColumn.strom": "Electrical",
  "propColumn.bau": "Construction & protection",
  "work.logistik": "Logistics",
  "log.sectionPackaging": "Logistics & packaging",
  "log.sectionPurchasing": "Purchasing",
  "attn.weight_missing": "Product weight missing",
  "attn.tariff_code_missing": "Customs tariff code missing",
  "attn.moq_missing": "Minimum order quantity missing",
  "otexts.emptyLanguage": "No text in this language yet.",
  "prov.section": "Origin & history",
  "prop.led_brand": "LED brand",
  "prop.led_type": "LED type",
  "prop.led_count": "LED count",
  "prop.lifetime": "Lifetime",
  "prop.lumen_maintenance": "Lumen maintenance",
  "prop.power_adjustable": "Adjustable power",
  "prop.voltage": "Supply voltage",
  "prop.frequency": "Mains frequency",
  "prop.control": "Control",
  "prop.control_gear_supply": "Control gear / scope of delivery",
  "prop.manufacturer_brand": "Manufacturer / brand",
  "prop.dc_output_voltage.atLuminaire": "DC operating voltage",
  "prop.dc_output_current.atLuminaire": "Operating current",
  "prop.wiring": "Wiring",
  "prop.cover_material": "Cover material",
  "prop.ambient_temperature": "Ambient temperature",
  "propValue.integriert": "integrated",
  "propValue.separat": "separate",
  "propValue.ohne": "none",
  "propValue.dali2": "DALI-2",
  "propValue.1_10v": "1–10 V",
  "propValue.casambi": "Casambi",
  "propValue.through_wiring": "Through wiring",
  "propValue.terminal_block": "Terminal block",
  "propValue.plug_connector": "Plug connector",
  "propValue.pmma": "PMMA",
  "propValue.glas": "Glass",
  "propValue.glas_gehaertet": "tempered glass",
  "propValue.opal_diffusor": "Opal diffuser",
  "propOperator.lt": "less than",
  "propOperator.le": "at most",
  "propOperator.gt": "greater than",
  "propOperator.ge": "at least",
  "infoStatus.offen": "open",
  "infoStatus.angefragt": "requested",
  "infoStatus.nicht_zutreffend": "not applicable",
  "infoStatus.bestaetigt": "confirmed",
  "work.aria": "Work areas",
  "work.technik": "Technical",
  "work.modell": "Model",
  "work.einkauf": "Purchasing",
  "work.texte": "Texts",
  "work.dokumente": "Documents",
  "work.herkunft": "Origin",
  "work.stateModelVariant": "{model} · variant",
  // ── Documents ──────────────────────────────────────────────────────────
  "doc.section": "Documents",
  "doc.loading": "Loading documents …",
  "doc.groupLichtplanung": "Lighting design / IES",
  "doc.groupDatenblatt": "Product datasheet",
  "doc.groupTreiber": "LED driver datasheet",
  "doc.groupZertifikate": "Certificates & conformity",
  "doc.groupAnleitung": "Product manual",
  "doc.groupPruefberichte": "Test reports & other files",
  "doc.more": "Show {n} more",
  "doc.less": "Show less",
  "doc.none": "not available",
  "doc.open": "Open",
  "doc.download": "Download",
  "doc.inherited": "inherited",
  "doc.inheritedFrom": "Maintained on product model {model} — applies to every variant.",
  "doc.dateLabel": "As of {date}",
  "doc.sizeKb": "{n} KB",
  "doc.sizeMb": "{n} MB",
  "doc.previous": "Previous versions ({n})",
  "doc.replacedOn": "replaced on {date}",
  "doc.add": "Add file",
  "doc.replace": "Replace",
  "doc.replaceInherited": "Replace on model",
  "doc.remove": "Remove",
  "doc.removeInherited": "Remove from model",
  "doc.removeAsk": "Really remove?",
  "doc.removeAskModel": "Remove for every variant?",
  "doc.removeYes": "Remove",
  "doc.adding": "New file",
  "doc.replacing": "Replaces: {name}",
  "doc.file": "File",
  "doc.formats": "PDF, IES, LDT, TXT, ZIP, PNG, JPG · {mb} MB at most",
  "doc.level": "Applies to",
  "doc.levelArticle": "this variant only",
  "doc.levelModel": "every variant of {model}",
  "doc.languageLabel": "Language",
  "doc.languageNone": "not specified",
  "doc.standLabel": "As of",
  "doc.upload": "Upload",
  "doc.uploading": "Uploading …",
  "doc.proven": "proven",
  "doc.editing": "Editing mode",
  "doc.editHint": "Add, replace and remove files.",
  "doc.editAria": "Edit documents",
  "doc.done": "Done",
  "list.compactAria": "Article selection",
  /* ── Central product record ───────────────────────────────────────────── */
  "akte.loading": "Loading product record …",
  "akte.back": "Back to product view",
  "akte.openWork": "Open work",
  "akte.openProposals": "{count} change proposals to decide",
  "akte.openChecks": "{count} values need review",
  "akte.openFlags": "{count} open review flags",
  "akte.jumpAria": "Sections of the product record",
  "akte.articleNumber": "Article number",
  "akte.category": "Category",
  "akte.family": "Product family",
  "akte.model": "Product model",
  "akte.variant": "Execution",
  "akte.sourceLabel": "Designation",
  "akte.lifecycle": "Assortment status",
  "akte.lifecycleUnset": "not determined",
  "akte.stockingType": "Assortment role",
  "akte.stockingUnset": "not determined",
  "stocking.lagerware": "Stock item",
  "stocking.projektware": "Project item",
  "list.stocking": "Assortment role",
  "list.stockingAll": "Any assortment role",
  "list.stockingOpen": "not yet determined",
  "akte.roles": "Roles",
  "akte.editBaseData": "Edit base data",
  "akte.dutyRate": "Duty rate",
  "akte.setDutyRate": "Record duty rate",
  "akte.dutyNeedsTariff": "A duty rate belongs to the customs tariff number. Without one there is nothing for it to apply to.",
  "akte.since": "since {date}",
  "akte.validFrom": "Valid from",
  "akte.validFromDate": "valid from {date}",
  "akte.validFromRevision": "valid from {date} · revision {revision}",
  "akte.herkunft_eingabe": "Input",
  "akte.herkunft_vorgabe": "Preset",
  "akte.herkunft_ergebnis": "Result",
  "akte.dutyFrom": "Duty rate {rate} % of tariff {code}",
  "akte.exw": "EXW",
  "akte.amount": "Amount",
  "akte.currency": "Currency",
  "akte.setPrice": "Record purchase price",
  "form.why.priceMissing": "Price missing -- please enter an amount above 0.",
  "form.why.dateMissing": "Valid-from date missing -- from when does the value apply?",
  "form.why.marginBlocked": "Undercuts the target margin -- only users with the exception right may release this.",
  "form.why.reasonMissing": "Reason for the exception missing.",
  "form.why.conflictsUnconfirmed": "Deviating releases of the variants -- please confirm deliberately.",
  "form.why.amountMissing": "Amount missing -- please enter a purchase price above 0.",
  "form.why.rateMissing": "Duty rate missing -- please enter a value from 0 to 100 %.",
  "akte.parameterSet": "Parameter set",
  "akte.noCalculation": "This article has not been calculated yet.",
  "akte.missing": "Not known yet: {items}",
  "akte.volumePerUnit": "Volume per unit",
  "akte.freight": "Freight",
  "akte.csef": "CSEF",
  "akte.duty": "Customs duty",
  "akte.landedUsd": "Landed cost",
  "akte.packagingAct": "Packaging act fee",
  "akte.packagingActPart": "{g} g {material} at {rate} EUR/t",
  "akte.electroAct": "Electrical equipment act fee",
  "akte.electroActFrom": "{kg} kg product weight at {rate} EUR/kg",
  "akte.electroActNote": "calculated allocation rate, not a measured collection fee",
  "akte.costBase": "Cost base",
  "param.packaging_act_cardboard_eur_per_kg": "Packaging fee cardboard/paper",
  "param.packaging_act_plastic_eur_per_kg": "Packaging fee plastic",
  "param.electro_act_eur_per_kg": "Electrical equipment fee",
  "calcInput.product_weight": "product weight",
  "calcInput.usd_eur_calc_rate": "calculation rate (discount too high)",
  "calcInput.usd_eur_safety_discount": "safety discount",
  "calcInput.sea_freight_usd_per_cbm": "sea freight rate",
  "calcInput.air_freight_usd_per_kg": "air freight rate",
  "calcInput.packaging_materials": "packaging materials",
  "calcInput.packaging_act_cardboard_eur_per_kg": "packaging fee rate cardboard",
  "calcInput.packaging_act_plastic_eur_per_kg": "packaging fee rate plastic",
  "calcInput.electro_act_eur_per_kg": "electrical equipment fee rate",
  "akte.landedEur": "Landed cost",
  "akte.atRate": "rate {rate}",
  "akte.showCalculation": "Show calculation",
  "akte.hideCalculation": "Hide calculation",
  "release.forModel": "Apply the list price to all versions of product model {model}",
  "release.appliesTo": "Applies to {n} SKUs",
  "release.conflictsTitle": "{n} differing release(s) on variants.",
  "release.conflictsText": "Nothing is silently overwritten: the new release applies from its date, the previous one remains as predecessor.",
  "release.conflictLine": "{sku}: released {alt} EUR from {date} → new {neu} EUR",
  "release.confirmConflicts": "Deliberately supersede the differing prices with this release",
  "stufen.netBeforeCommission": "Net revenue before commission",
  "stufen.netBeforeCommissionFrom": "Cost base {k} EUR ÷ (1 − {m} %) -- secures the target margin",
  "stufen.commissionMeaning": "Commission of an external intermediary on the commissionable net selling price -- on top of the target margin",
  "stufen.roundedUp": "Raised to the next {step} EUR step so the target margin of {m} % is not undercut.",
  "stufen.salesNet": "Commissionable net selling price",
  "stufen.salesNetFrom": "Net revenue before commission ÷ (1 − {p} %)",
  "stufen.commissionAmount": "Commission",
  "stufen.commissionAmountFrom": "{p} % of {s} EUR -- not of the cost base",
  "stufen.netAfterCommission": "Net revenue after commission",
  "stufen.netAfterCommissionMeaning": "remains after paying the commission -- the target margin of {m} % is fully preserved",
  "stufen.roundedNormal": "Rounded commercially to the {step} EUR step; the target margin of {m} % is kept.",
  "stufen.listPriceRaw": "List price before rounding",
  "stufen.listPriceFactor": "List price factor",
  "stufen.marginAfterRounding": "Margin after commission and rounding",
  "kalk.rounding": "Rounding",
  "kalk.listPriceCalc": "List price calculation",
  "kalk.expand": "Expand list price calculation",
  "kalk.collapse": "Collapse list price calculation",
  "satz.divisorFrom": "stored as divisor {divisor} · list price factor × {faktor}",
  "satz.scopeFamily": "Applies to product family {family} -- all articles of this family. The family deviation is changed, not an article value. This article is recalculated afterwards.",
  "satz.nextRevision": "Revision {revision} already exists for {date} -- revision {next} will be saved. Earlier revisions and their calculations remain unchanged.",
  "satz.scopeCentral": "Applies centrally to all articles without a family deviation -- as a new revision of the parameter version from {date}. No article value. Only this article is recalculated afterwards.",
  "strecke.discountBasis": "Discount basis",
  "strecke.discountBasisFrom": "Discount basis {basis} % · list price factor × {faktor}",
  "vergleich.title": "Transport comparison",
  "importweg.title": "List price by import route",
  "importweg.section": "List price",
  "importweg.none": "Not calculated yet -- use “Recalculate” in the list price calculation below.",
  "importweg.laufzeit": "Transit time",
  "importweg.laufzeitTage": "approx. {n} days",
  "importweg.laufzeitWoche": "approx. 1 week",
  "importweg.laufzeitWochen": "approx. {n} weeks",
  "importweg.nutzen_sea": "Most economical route",
  "importweg.nutzen_train": "Standard route",
  "importweg.nutzen_air": "Fastest route",
  "importweg.hinweis": "The list prices are calculated values based on the current parameters. They are not released yet and are not binding customer prices.",
  "vergleich.note": "Internal comparison. Only the active route can be released; comparison prices are neither stored nor published.",
  "vergleich.active": "active",
  "vergleich.notComputable": "not computable",
  "vergleich.rateMissing": "freight rate missing",
  "vergleich.inputsMissing": "inputs missing",
  "vergleich.airRateMissing": "Air freight rate missing",
  "vergleich.masterDimensionsMissing": "Master carton dimensions missing",
  "vergleich.masterGrossMissing": "Master carton gross weight missing",
  "vergleich.masterUnitsMissing": "Units per master missing",
  "vergleich.divisorMissing": "Volumetric divisor missing",
  "luft.master": "Master carton",
  "luft.masterValue": "{l} × {b} × {h} mm",
  "luft.gross": "Master gross weight",
  "luft.units": "Units per master",
  "luft.unitsShort": "{n} pcs/master",
  "luft.divisor": "Volumetric divisor",
  "luft.volumetric": "Volumetric weight",
  "luft.chargeable": "Chargeable weight",
  "luft.basis_volumen": "Basis: volumetric weight -- higher than the actual weight",
  "luft.basis_gewicht": "Basis: actual weight -- at least as high as the volumetric weight",
  "luft.basisShort_volumen": "volumetric weight",
  "luft.basisShort_gewicht": "actual weight",
  "luft.chargeableShort": "chargeable {kg} kg ({basis})",
  "luft.rate": "Air freight rate",
  "luft.rateMissing": "not maintained",
  "luft.freightMaster": "Air freight per master",
  "luft.freightUnit": "Air freight per unit",
  "luft.freightShort": "{rate} USD/kg · master {master} USD · per unit {unit} USD",
  "calcInput.master_dimensions": "Master carton dimensions",
  "calcInput.master_gross_weight": "Master carton gross weight",
  "calcInput.master_unit_count": "Units per master carton",
  "calcInput.air_volumetric_divisor_cm3_per_kg": "Volumetric divisor",
  "param.air_volumetric_divisor_cm3_per_kg": "Air freight volumetric divisor",
  "vergleich.diff": "{value} EUR vs. active",
  "vergleich.stale": "Inputs have changed since the last calculation – “Recalculate” updates the active price.",
  "vergleich.newerSet": "A newer parameter version applies since the last calculation – “Recalculate” applies it to the calculation and the comparison.",
  "vergleich.error": "The comparison could not be calculated.",
  "kalk.showDetails": "Calculation details",
  "kalk.hideDetails": "Close calculation details",
  "kalk.basis": "Basis",
  "kalk.steps": "Calculation steps",
  "kalk.releasedOther": "released: {value} EUR from {date}",
  "strecke.exw": "EXW USD",
  "strecke.landed": "HH Landed",
  "strecke.commission": "Commission",
  "strecke.minMargin": "Target margin",
  "strecke.edit": "Change {what}",
  "strecke.recalc": "Apply and recalculate",
  "strecke.exwHint": "New price level from the chosen date; the previous price remains as predecessor. The calculation is then rerun.",
  "strecke.freightHint": "You choose the transport mode. The freight rate comes from the valid parameter set and stays there.",
  "kette.label": "Calculation from purchase price to list price",
  "kette.ek": "Purchase",
  "kette.rate": "Calculation rate",
  "kette.freight": "Freight",
  "kette.duty": "Duty",
  "kette.packagingAct": "Packaging levy",
  "kette.electroAct": "WEEE levy",
  "kette.costBase": "Landed cost",
  "kette.margin": "Margin",
  "kette.listPrice": "List price",
  "akte.recalculate": "Recalculate",
  "akte.calculatedAt": "calculated on {date} by {user}",
  "akte.minimumMargin": "Target margin",
  "akte.resultingMargin": "Resulting margin",
  "akte.minimumWas": "target margin {value} %",
  "akte.belowMinimumTitle": "Target margin undercut.",
  "akte.belowMinimumWarning": "An exceptional release is possible, but requires a reason and remains visible permanently.",
  "akte.belowMinimumReason": "Reason for the exception",
  "akte.belowMinimumRecorded": "Undercuts the target margin: {margin} % instead of {minimum} %.",
  "akte.releaseException": "Exceptional release below target margin",
  "akte.belowMinimumBlocked": "Release not possible.",
  "akte.belowMinimumBlockedText": "The price undercuts the applicable target margin.",
  "akte.exceptionRecorded": "Exceptional release.",
  "akte.marginFromFamily": "deviation of family {family}",
  "akte.marginStandard": "global standard",
  "akte.marginWhereEdited": "Not editable per article: the target margin applies globally or per product family and is maintained in the parameter set.",
  "akte.calculatedListPrice": "Calculated list price",
  "akte.releases": "Released list prices",
  "akte.noRelease": "No list price has been released for this article yet.",
  "akte.releasedPrice": "List price",
  "akte.releasedBy": "Released by",
  "akte.release": "Release list price",
  "akte.note": "Note",
  "akteSection.identitaet": "Article identity",
  "akteSection.varianten": "Variant space",
  "varianten.lead": "Which versions exist — not what this article is. This SKU's own value appears in the technical sections.",
  "varianten.standard": "Standard",
  "varianten.optional": "Optional",
  "varianten.noStandard": "equally ranked versions, no named standard",
  "varianten.empty": "No variant space is recorded for this article.",
  "varianten.source": "Source",
  "varianten.noSku": "A version from this space is not yet an article. It gains its own article number only when it is actually to be offered.",
  "akteSection.lichttechnik": "Photometrics",
  "akteSection.stromtechnik": "Electrical",
  "akteSection.konstruktion": "Construction",
  "akteSection.verpackung": "Packaging & logistics",
  "akteSection.zoll": "Customs",
  "akteSection.einkauf": "Purchasing",
  "akteSection.zuschlaege": "Surcharges & freight",
  "akteSection.importkalkulation": "Import calculation",
  "akteSection.verkaufspreis": "Selling price",
  "nav.parameters": "Calculation",
  "param.title": "Calculation parameters",
  "param.card.waehrung": "Currency",
  "param.card.fracht": "Freight",
  "param.card.verpackungsg": "Packaging act",
  "param.card.elektrog": "Electrical equipment act",
  "param.usd_eur_safety_discount": "Safety discount",
  "param.usd_eur_calc_rate": "USD/EUR calculation rate",
  "param.calcRateFrom": "{ref} − {ab} safety discount",
  "param.sea_freight_usd_per_cbm": "Sea",
  "akte.transportMode": "Transport mode",
  "akte.freightRate": "Freight rate",
  "transport.sea": "Sea",
  "transport.train": "Train",
  "transport.air": "Air",
  "param.air_freight_usd_per_kg": "Air",
  "param.electroActNote": "Calculated allocation rate for fees and related costs — greenius has had no EAR collection so far; this is not a rate derived from actual collections.",
  "param.edit": "Edit calculation parameters",
  "param.save": "Save",
  "param.cancel": "Cancel",
  "param.why.readyRevision": "Revision {revision} already exists for {date} -- revision {next} will be saved on its basis. Earlier revisions are kept.",
  "param.unchanged": "No change compared with revision {revision} of the version from {date} -- nothing was saved.",
  "param.savedRevision": "Saved as revision {revision} of the version from {date}. Earlier revisions remain in the history.",
  "param.replacedByRevision": "corrected by revision {revision}",
  "param.savedAt": "saved",
  "param.revision": "Revision",
  "param.validFromRevision": "Version in force since {date} · revision {revision} · saved {at} · {by}",
  "param.why.ready": "Saves exactly one new version, valid from {date}. The previous one is kept.",
  "param.why.noDate": "Valid-from date missing -- from when does the new version apply?",
  "param.why.invalid": "{field}: please enter a valid number (0 or more; a maintained value cannot be cleared).",
  "param.why.noChange": "No change yet -- change at least one value.",
  "param.validFromShort": "valid from",
  "param.newValidFrom": "Valid from",
  "param.history": "History · {n} versions",
  "param.status": "Status",
  "param.values": "Values",
  "param.current": "in force",
  "param.superseded": "superseded",
  "param.future": "prepared",
  "param.past": "past",
  "param.inUse": "used in calculations",
  "param.usd_eur_rate": "USD/EUR reference rate",
  "param.rail_freight_usd_per_cbm": "Train",
  "param.csef_percent": "CSEF",
  "param.commission_percent": "Commission",
  "param.minimum_margin_percent": "Target margin",
  "param.list_price_divisor": "List-price divisor",
  "param.rounding_step_eur": "Rounding step",
  "calcInput.exw": "Purchase price",
  "calcInput.volume_per_unit": "Volume per unit",
  "calcInput.tariff_code": "Customs tariff number",
  "calcInput.duty_rate": "Duty rate",
  "calcInput.usd_eur_rate": "Exchange rate",
  "calcInput.rail_freight_usd_per_cbm": "Rail freight",
  "calcInput.csef_percent": "CSEF",
  "calcInput.commission_percent": "Commission",
  "calcInput.minimum_margin_percent": "Target margin",
  "calcInput.list_price_divisor": "List-price divisor",
  "calcInput.rounding_step_eur": "Rounding step",
  "detail.openDossier": "Product record",
  "list.createArticle": "+ New product",
  "neu.title": "Create product",
  "neu.articleNumber": "Article number",
  "neu.articleNumberHint": "The article number is the identity of the article and cannot be changed afterwards.",
  "neu.familyChoose": "Please choose",
  "neu.familyHint": "The family determines the category and the target margin.",
  "neu.categoryFollows": "Category: {category}",
  "neu.shortDescription": "Designation",
  "neu.shortDescriptionHint": "Optional. A source designation, not a product name – the product name is written later as editorial text.",
  "neu.roles": "Roles",
  "neu.stepCategory": "Main category",
  "neu.stepCategoryHint": "It narrows the family list. The article's category follows from the family.",
  "neu.stepFamily": "Product family",
  "neu.stepBase": "Base product",
  "neu.stepBaseHint": "What already exists in this family — for guidance on number and description.",
  "neu.baseNone": "No article has been created in this family yet.",
  "neu.stepVariant": "Variant parameters",
  "neu.stepVariantHint": "Which versions of the base product exist.",
  "neu.variantNoBase": "Without a base product there is nothing to show here.",
  "neu.variantNone": "No variant space has been recorded for this product.",
  "neu.stepIdentity": "Identity",
  "neu.stepSummary": "Summary",
  "neu.modelAxesHint": "This product belongs to product model {model}. The chosen executions are recorded as a variant — without copying any properties.",
  "neu.variantTaken": "This execution already exists: {number}. Change one value — two products with the same execution would be the same product.",
  "neu.variantTakenVirtual": "a variant not yet materialised",
  "neu.modelFromBase": "as in basis",
  "neu.modelTitle": "Product model",
  "neu.modelKnowledge": "Applies through product model {model} — not copied, the provenance stays with the model.",
  "neu.modelChanges": "Variant changes",
  "neu.modelUnchanged": "Unchanged from the basis",
  "neu.modelSkuOwn": "SKU-specific",
  "neu.modelNoChange": "No execution differs from the basis.",
  "neu.stepKind": "Kind of entry",
  "neu.stepKindHint": "An execution of an existing product — or a product without a model to follow?",
  "neu.kindVariant": "Variant of an existing product",
  "neu.kindVariantHint": "{n} products of this family are available as a basis.",
  "neu.kindVariantNone": "This family has no product yet to build on.",
  "neu.kindPlain": "Product without a basis",
  "neu.kindPlainHint": "Number, description and roles are entered by hand.",
  "neuCase.A": "Case A — variant of an existing product.",
  "neuCase.B": "Case B — new product in an existing product family.",
  "neuCase.C": "Case C — first product of a newly created product family.",
  "neu.familyAdd": "+ New product family",
  "neu.categoryAdd": "+ New main category",
  "neu.categoryAddTitle": "New main category",
  "neu.categoryCode": "Category code",
  "neu.categoryCodeHint": "Short and unique, e.g. triproof. It structures the whole catalogue – only create one when a genuinely new product world begins.",
  "neu.categoryName": "Category name",
  "neu.categoryCreate": "Create main category",
  "neu.categorySimilar": "A similar main category already exists:",
  "neu.categorySimilarHint": "Check whether it is the same product world. HOMEBASE never merges and never renames.",
  "neu.familyAddTitle": "New product family",
  "neu.familyCode": "Family code",
  "neu.familyCodeHint": "Short and unique, e.g. HB UGR. Used only once across all categories.",
  "neu.familyName": "Product family name",
  "neu.familyCreate": "Create product family",
  "neu.familyArticles": "{n} products",
  "neu.familySimilar": "A similar product family already exists:",
  "neu.familySimilarHint": "Check whether it is the same series. HOMEBASE merges nothing and renames nothing.",
  "neu.numberBase": "The basis is {number}. Numbering differs per family — HOMEBASE therefore proposes none.",
  "neu.numberTaken": "This article number already exists in this family.",
  "neu.shortDescriptionFromBase": "Taken verbatim from {number} — please review and adjust.",
  "neu.shortDescriptionUnchanged": "The description is unchanged although executions differ.",
  "neu.shortDescriptionMissing": "Without a description the product cannot be recognised in any list.",
  "neu.twinTitle": "Possible duplicate in substance:",
  "neu.twinHint": "Same family, same execution. Check whether this product already exists.",
  "neu.inheritTitle": "Will be carried over",
  "neu.inheritFamily": "Product family as the primary assignment",
  "neu.inheritCategory": "Main category — it follows the family",
  "neu.inheritDescription": "Description, verbatim from {number}",
  "neu.inheritModel": "Membership in product model {model} with the chosen executions",
  "neu.notPropertiesModel": "Technical properties — they stay with the basis; shared knowledge belongs to the model",
  "neu.notProperties": "Technical properties",
  "neu.notTexts": "Product texts",
  "neu.notSupplier": "Suppliers, purchase prices and terms",
  "neu.notLogistics": "Packaging and logistics",
  "neu.notCompliance": "Certifications",
  "neu.openTitle": "To be settled afterwards",
  "neu.openLifecycle": "Assortment status — the product starts as not released; releasing it is a decision of its own",
  "neu.openProperties": "Record the technical properties in the product file",
  "neu.openFamily": "Complete the new product family with its remaining details",
  "neu.notRecordedTitle": "NOT created along with it",
  "neu.notRecordedHint": "Base product and version serve as guidance only. The new article is created with number, family, description and roles — a descent from another article is not recorded.",
  "neu.back": "Back",
  "neu.next": "Next",
  "neu.create": "Create",
  /* ── Technical data: compact view and editing mode ────────────────────── */
  "tk.editAria": "Edit technical data",
  "tk.editing": "Editing technical data",
  "tk.editHint": "Typed values apply only once saved. Confirming, withdrawing and removing take effect immediately — saving is not a professional confirmation.",
  "tk.save": "Save changes",
  "tk.savedAll": "{n} change(s) saved.",
  "tk.savedSome": "{ok} saved, {offen} not. The remaining entries are still here.",
  "tk.savedNone": "Nothing saved. Your entries are still here.",
  "tk.unconfirmed": "Not yet confirmed",
  "tk.savedUnconfirmed": "Saved · Not yet professionally confirmed",
  "tk.done": "Done",
  "tk.breaker": "Fixtures per circuit breaker",
  "tk.breakerRow": "Fixtures per breaker {breaker}",
  "tk.breakerEmpty": "Not determined.",
  "tk.breakerCount": "{n} fixtures",
  "tk.breakerCharacteristic": "Characteristic",
  "tk.breakerCurrent": "Rated current",
  "tk.breakerMax": "Maximum count",
  "tk.breakerErrCurrent": "Please enter a rated current as a whole number of at least 1.",
  "tk.breakerErrCount": "Please enter a count as a whole number of at least 1.",
  "tk.changed": "Changed, not saved",
  "tk.invalid": "Invalid entry",
  "tk.confirmBusy": "Save first, then confirm.",
  "tk.sources": "Sources for this group",
  "tk.standWert": "1 technical value",
  "tk.standWerte": "{n} technical values",
  "tk.standBestaetigt": "{n} professionally confirmed",
  "tk.standBefund": "1 finding",
  "tk.standBefunde": "{n} findings",
  "tk.standVorschlag": "1 open proposal",
  "tk.standVorschlaege": "{n} open proposals",
  "tk.inherited": "Model",
  "tk.confirmAtModel": "Confirmed on the product model",
  "tk.confirmStored": "Confirm the stored value …",
  "tk.clarifyReason": "Why is this not a contradiction?",
  "tk.clarifyNote": "The reason stays in the log. If the source changes later, the contradiction reappears.",
  "tk.clarifySave": "Confirm",
  "tk.fromExecution": "Version",
  "tk.fromExecutionTitle": "Value of the model version this SKU belongs to. It was adopted on assignment and has belonged to the SKU since.",
  "tk.ownValue": "own value",
  "tk.ownValueTitle": "Recorded on this SKU – neither inherited from the product model nor adopted from the version.",
  "tk.inheritedTitle": "Inherited from product model {model}. Changing it creates a value of this SKU; the model stays unchanged.",
  "tk.errEmpty": "Please enter a value or remove the value.",
  "tk.errNumber": "Please enter a number.",
  "tk.errInteger": "Please enter a whole number.",
  "tk.errOrder": "The upper value must not be smaller than the lower one.",
  "tk.leaveTitle": "Unsaved changes",
  "tk.leaveText": "There are changes in the technical data that have not been saved.",
  "tk.leaveSave": "Save and continue",
  "tk.leaveDiscard": "Discard and continue",
  "tk.leaveStay": "Keep editing",
  "trash.open": "Trash",
  "trash.openTitle": "View deleted products",
  "trash.back": "Back",
  "trash.title": "Trash",
  "trash.intro": "Products that were put away. They appear in no product list, remain fully intact and can be restored.",
  "trash.empty": "The trash is empty.",
  "trash.number": "Article number",
  "trash.description": "Description",
  "trash.family": "Product family",
  "trash.deletedAt": "Deleted on",
  "trash.deletedBy": "Deleted by",
  "trash.reason": "Reason",
  "trash.noDescription": "no description",
  "trash.restore": "Restore",
  "trash.purgeOpen": "Delete permanently …",
  "trash.preflightTitle": "Delete permanently: {number}",
  "trashKind.anlage": "created on entry",
  "trashKind.fachdaten": "product knowledge",
  "trashKind.historie": "operational history",
  "trashKind.nachweis": "record, survives deletion",
  "trashStatus.safe": "Safe to delete",
  "trashStatus.requires_review": "Review required",
  "trashStatus.blocked": "Cannot be deleted",
  "trash.confirmLabel": "Type {number} to confirm:",
  "trash.purgeConfirm": "Delete permanently",
  "trash.blockedHint": "The article stays in the trash. Only what carries no operational history and no product knowledge is deleted permanently.",
  "trash.close": "Close",
  "akte.trash": "Move to trash",
  "akte.trashTitle": "Put the product away",
  "akte.trashHeading": "Move {number} to the trash?",
  "akte.trashText": "The product disappears from every product list. Properties, texts and relations are kept, and it can be restored at any time.",
  "akte.trashReason": "Reason",
  "akte.trashNote": "Note",
  "akte.trashNotePlaceholder": "What went wrong?",
  "akte.trashConfirm": "Move to trash",
  "akte.trashCancel": "Cancel",
  "deletionReason.irrtuemlich_angelegt": "Created by mistake",
  "deletionReason.dublette": "Duplicate",
  "deletionReason.testartikel": "Test article",
  "deletionReason.anlage_abgebrochen": "Entry abandoned",
  "deletionReason.sonstiger_grund": "Other reason",
  "error.article_number_taken": "This article number is already in use.",
  "error.article_number_in_trash": "This article number is in the trash. The article can be reviewed and restored there.",
  "error.article_number_previously_deleted": "This article number was used before and deleted permanently. Reusing it must be confirmed explicitly.",
  "error.already_trashed": "This product is already in the trash.",
  "error.not_trashed": "This product is not in the trash.",
  "error.reason_required": "Please choose a reason.",
  "error.invalid_reason": "That reason does not exist.",
  "error.note_required": "This reason requires a note.",
  "error.delete_blocked": "This product cannot be deleted permanently. The deletion report states why.",
  "error.confirmation_mismatch": "The article number must be typed exactly to confirm.",
  "neu.numberInTrash": "{number} is in the trash. The article can be reviewed and restored there.",
  "neu.numberInTrashOpen": "Open trash",
  "error.invalid_article_number": "Article number: letters, digits, dot, hyphen, slash, plus.",
  "error.invalid_family": "That product family does not exist.",
  "error.invalid_article": "That article does not exist.",
  "error.invalid_lifecycle": "That range status does not exist.",
  "error.invalid_stocking_type": "That stocking type does not exist.",
  "error.invalid_relation_type": "That relation type does not exist.",
  "error.invalid_characteristic": "Characteristic: B, C, D or A per IEC 60898-1.",
  "error.relation_exists": "That relation already exists.",
  "error.self_relation": "An article does not relate to itself.",
  "error.capacity_exists": "An entry already exists for this characteristic and current.",
  "error.asked_at_required": "For \u201crequested\u201d the date of the request is required.",
  "error.lifecycle_missing": "The range status \u201cNot yet released\u201d is missing from the vocabulary. That is a setup fault, not an input error.",
  "models.category": "Main category",
  "models.sectionPlacement": "Placement",
  "models.sectionModel": "Product model",
  "models.familyAfterCategory": "choose a main category first",
  "models.codeHint": "Short and unique, e.g. TP154.",
  "models.nameHint": "Descriptive, e.g. TP PRO TP154.",
  "models.textsLater": "Descriptions and long texts belong to the finished model – they live in its “Texts & documents” tab.",
  "neu.model": "Product model",
  "neu.modelChoose": "Please choose",
  "neu.modelHint": "The technical platform within this product family. It determines which variant parameters are offered.",
  "neu.modelOnly": "Product model {model} — the only one in this product family.",
  "neu.modelNone": "There is no product model for this product family yet. One can be created under Product models; without a model this step only shows the variant space of the base product.",
  "neu.modelNoAxes": "Product model {model} has no variant parameters yet. Axes and permitted values can be maintained under Product models.",
  "neu.modelNoneHere": "There is no product model for {family} yet.",
  "neu.modelAdd": "+ New product model",
  "neu.modelAddTitle": "New product model",
  "neu.modelForFamily": "It belongs to the product family {family}.",
  "neu.modelCode": "Model code",
  "neu.modelCodeHint": "Short and unique, e.g. HB200-U. The technical platform, not the article number.",
  "neu.modelName": "Product model name",
  "neu.modelCreate": "Create product model",
  /* Produktmodelle 002 -- Modellseite V2 und sichere Modellfreigabe. */
  "mtab.overview": "Overview",
  "mtab.common": "Shared data",
  "mtab.executions": "Versions",
  "mtab.content": "Texts & documents",
  "mtab.history": "History",
  "mp.proposal": "Proposal – please confirm",
  "mp.editMaster": "Edit master data",
  "mp.kpiExecutions": "versions",
  "mp.kpiSkus": "articles/SKUs",
  "mp.kpiCalculated": "calculated",
  "mp.kpiReleased": "price released",
  "mp.note": "Note",
  "mp.codeReadonly": "The model code {code} stays unchanged here: it is part of released model texts and model price releases.",
  "mp.familyReadonly": "The product family is not changed here – all assigned articles would have to move with it.",
  "mp.statusTitle": "Status per version",
  "mp.commonLabel": "Shared:",
  "mp.appliesToAll": "applies to all existing versions",
  "mp.furtherValue": "Further available value ({prop}):",
  "mp.noExecution": "no version created",
  "mp.noExecutionCell": "No version exists for this combination.",
  "mp.stTechnik": "Technical",
  "mp.stEinkauf": "Purchasing",
  "mp.stZoll": "Customs",
  "mp.stVerpackung": "Packaging",
  "mp.stKalkulation": "calculable",
  "mp.inheritedFromModel": "inherited from the product model",
  "mp.priceReleased": "€{amount} released",
  "mp.priceCalculated": "€{amount} calculated",
  "mp.priceNone": "no list price",
  "mp.colSupplier": "Supplier",
  "mp.colExw": "EXW",
  "mp.colCalculable": "Calculable",
  "mp.colLatest": "Latest calculation",
  "mp.colCalculated": "List price calculated",
  "mp.colReleased": "List price released",
  "mp.missingShort": "Missing today:",
  "mp.inheritedShort": "(inherited)",
  "mp.matrixRows": "Rows: {a}",
  "mp.matrixCols": "Columns: {a}",
  "mp.packOwn": "own version",
  "mp.latestIncomplete": "(incomplete)",
  "mp.noCalculation": "not calculated",
  "mp.notOnModel": "not on the model",
  "mp.commonValue": "Shared value",
  "mp.axisProperty": "Version property",
  "mp.axisValues": "differs per version",
  "mp.axisWhere": "maintained in the “Versions” tab",
  "mp.axisHasModelValue": "Version property with model value",
  "mp.axisHasModelValueTitle": "This property distinguishes the versions yet carries a shared model value. Click to remove it – the versions keep their own values.",
  "mp.toggleOnTitle": "Adopt this observed value as the shared model value",
  "mp.toggleOffTitle": "Remove this shared model value again",
  "mp.toggleNoValue": "There is no observed value that could be adopted.",
  "mp.toggleAmbiguous": "The versions carry different values – please decide in the preview.",
  "mp.chooseCandidate": "several different values",
  "mp.ambiguousNote": "The versions carry different values. A shared value is not created automatically here – either it is a version property, or the differing values need correcting first.",
  "mp.removeResult": "no shared value any more",
  "mp.removeLosing": "{n} versions show this value inherited today – they will not afterwards. Their own values stay untouched.",
  "mp.removeNoLoss": "No version inherits this value – removing it changes nothing on the SKUs.",
  "mp.coverage": "applies to {n} of {total} versions",
  "mp.onlyOnSkus": "maintained on {n} of {total} versions only",
  "mp.ownValues": "{n} with own value",
  "mp.ownSame": "{n} with own, identical value",
  "mp.dropOwnTitle": "These versions carry the same value a second time. Click to drop the duplicate own value – afterwards they inherit the model value.",
  "mp.dropOwnTitel": "Drop duplicate value",
  "mp.dropOwnFuture": "These versions inherit the model value afterwards:",
  "mp.dropOwnConfirm": "Drop selected own values",
  "mp.dropOwnOriginLost": "Own provenance is lost: {eigen} – afterwards the version shows {modell}.",
  "mp.dropOwnSegmentLost": "The recorded source wording “{segment}” is lost with it.",
  "mp.dropOwnQualityBlocked": "Blocked: this version carries the identical value with a stronger confirmation than the product model. Please confirm the model value first. Only then can the own value be dropped.",
  "mp.dropOwnClarificationBlocked": "Blocked: a clarification still exists for this own value. Please resolve it before the own value can be dropped.",
  "mp.dropOwnProposalBlocked": "Blocked: an open value proposal still exists for this own value. Please resolve it before the own value can be dropped.",
  "mp.dropOwnNoneFree": "None of these versions can drop its own value at the moment.",
  "mp.dropOwnNote": "{n} version(s) drop their own value and inherit it from the model afterwards: {list}. The shared model value stays unchanged.",
  "mp.ownDiffers": "{n} with differing value",
  "mp.adopt": "Adopt as shared value …",
  "mp.adoptPack": "Adopt this version as shared packaging …",
  "mp.packMaster": "Master carton {l} × {b} × {h} mm",
  "mp.packNoMaster": "no master carton",
  "mp.packUnits": "{n} units per master",
  "mp.packRevision": "Rev. {n}",
  "mp.commonNote": "Values on the product model apply to every version without its own value. An own value on the version takes precedence and stays visible.",
  "mp.cardMasse": "Dimensions & weight",
  "mp.cardVerpackung": "Packaging & master carton",
  "mp.cardZoll": "Customs",
  "mp.cardTechnik": "Technical data",
  "mp.cardZert": "Certifications / conformities",
  "mp.cardNachweise": "Evidence",
  "mp.cardEmpty": "No shared values yet.",
  "mp.onModel": "On the model",
  "mp.ownStand": "own version",
  "mp.inheritsFromModel": "inherits from the model",
  "mp.none": "none",
  "mp.tariffOnModel": "Tariff code on the model",
  "mp.differsNote": "Differ per version (see the Versions tab): {list}",
  "mp.evidenceNotModel": "Evidence is currently attached to each version only – it cannot yet be maintained on the model (follows with BLOCK 003).",
  "mp.editDirect": "Maintain shared data directly",
  "mp.refresh": "Reload coverage",
  "mp.adoptTitle": "Preview: shared value on the model",
  "mp.adoptFuture": "Future model value:",
  "mp.adoptKeepsOwn": "keeps its own value: {value}",
  "mp.adoptInherits": "will inherit from the model",
  "mp.adoptNote": "The values of the versions remain unchanged – nothing is deleted. The model value starts unreviewed.",
  "mp.adoptFromArticle": "Adopt values from an existing article",
  "mp.adoptFromArticleNote": "An existing article of this model can be the source for shared model values. Only what can hold model-wide is proposed – never version properties.",
  "mp.adoptSource": "Source article",
  "mp.adoptAnalyse": "Show values",
  "mp.adoptCandidates": "{n} adoptable values from {number}",
  "mp.adoptNothing": "Nothing can be adopted from this article at the moment.",
  "mp.adoptValues": "{n} values",
  "mp.adoptSkipped": "Not proposed:",
  "mp.adoptWhyAxis": "version property",
  "mp.adoptWhyModel": "the model already carries a value",
  "mp.adoptWhyLevel": "not kept at model level",
  "mp.adoptSelected": "Adopt {n} values",
  "mp.adoptedCount": "{n} values adopted as shared model values.",
  "mp.adoptConfirm": "Adopt",
  "mp.deviationsTitle": "How the versions differ",
  "mp.deviationsNote": "Only properties with differing values. Shared values are listed under “Shared data”.",
  "mp.colProperty": "Property",
  "mp.editAxes": "Edit version properties",
  "mp.documentsTitle": "Documents of the product line",
  "mp.editDocuments": "Edit documents",
  "mp.documentsNote": "Documents stored here apply to all versions and appear as inherited on every SKU.",
  "mp.historyEmpty": "No changes logged yet.",
  "mp.colWhen": "When",
  "mp.colWho": "User",
  "mp.colArea": "Area",
  "mp.colAction": "Action",
  "mp.colChange": "Change",
  "mp.blocker.no_calculation": "no calculation",
  "mp.blocker.calculation_incomplete": "latest calculation incomplete",
  "mp.blocker.basis_incomplete": "calculation basis incomplete today",
  "mpArea.master": "Master data",
  "mpArea.executions": "Versions",
  "mpArea.logistics": "Logistics & customs",
  "mpArea.properties": "Properties",
  "mpArea.common": "Shared data",
  "modelLog.created": "Model created",
  "modelLog.deleted": "Model deleted",
  "modelLog.code": "Model code changed",
  "modelLog.name": "Model name changed",
  "modelLog.family": "Product family changed",
  "modelLog.provenance": "Model confirmed",
  "modelLog.note": "Note changed",
  "modelLog.dimension_added": "Version property added",
  "modelLog.dimension_removed": "Version property removed",
  "modelLog.dimension_value_added": "Allowed value added",
  "modelLog.dimension_value_removed": "Allowed value removed",
  "modelLog.variant_created": "Version created",
  "modelLog.variant_deleted": "Version removed",
  "modelLog.variant_article": "Article of version changed",
  "modelLog.variant_values": "Values of version changed",
  "modelLog.focus_rank": "Focus changed",
  "modelLog.article_assigned": "Article assigned",
  "modelLog.article_unassigned": "Article unassigned",
  "modelLog.common_value_adopted": "Shared value adopted",
  "modelLog.tariff_code": "Tariff code set",
  "modelLog.packaging_created": "Packaging version created",
  "modelLog.packaging_deleted": "Packaging version removed",
  "modelLog.packaging_reason": "Change reason changed",
  "modelLog.packaging_provider": "Provider changed",
  "modelLog.packaging_origin": "Packaging origin changed",
  "modelLog.packaging_method": "Packaging method changed",
  "modelLog.packaging_quality": "Packaging review status changed",
  "modelLog.packaging_residual": "Residual material confirmed",
  "modelLog.level_added": "Packaging level added",
  "modelLog.level_changed": "Packaging level changed",
  "modelLog.level_removed": "Packaging level removed",
  "modelLog.materials_set": "Packaging materials set",
  "modelLog.test_added": "Packaging test added",
  "modelLog.test_removed": "Packaging test removed",
  "modelLog.property_created": "Property value created",
  "modelLog.property_deleted": "Property value removed",
  "modelLog.property_value": "Property value changed",
  "modelLog.property_quality": "Review status changed",
  "modelLog.property_origin": "Origin changed",
  "modelLog.property_method": "Method changed",
  "modelLog.property_provenance": "Provenance changed",
  "modelLog.property_befund_geklaert": "Finding clarified",
  "release.previewTitle": "Margin check per version",
  "release.notVerifiable": "{n} version(s) cannot be checked – a model release requires a determinable margin for every version.",
  "release.colSku": "SKU",
  "release.colCostBase": "Cost base",
  "release.colPrice": "Release price",
  "release.colMargin": "Margin",
  "release.colTarget": "Target margin",
  "release.colStatus": "Status",
  "release.statusOk": "OK",
  "release.statusBelow": "below target margin",
  "release.statusNotVerifiable": "NOT VERIFIABLE",
  "form.why.modelNotVerifiable": "{n} version(s) not verifiable – model release blocked.",
  "error.model_release_not_verifiable": "At least one version cannot be checked – a model release requires a determinable margin for every version.",
  "error.not_a_candidate": "This value can no longer be adopted as a shared value.",
  "error.axis_property": "In this product model this property distinguishes the versions and therefore cannot be a shared model value.",
  "error.common_value_exists": "The product model carries a shared value for this property. It must be removed before the property can distinguish the versions.",
  "error.value_differs": "This version carries a different value than the model. A differing value is not removed here – a person decides about it.",
  "error.no_own_value": "This version carries no own value for this property.",
  "error.quality_would_drop": "This version carries the identical value with a stronger confirmation than the product model. Please confirm the model value first.",
  "error.clarification_or_proposal_present": "A clarification or an open value proposal still exists for this own value. Please resolve it first.",
  /* Produktmodelle 002A -- Artikelzuordnung und sicheres Loeschen. */
  "mp.noArticle": "No article assigned",
  "mp.kpiOpen": "{n} versions without article number",
  "mp.moreActions": "More actions",
  "mz.open": "Assign article",
  "mz.title": "Assign an article to this version",
  "mz.existing": "Assign existing article",
  "mz.new": "Create new article",
  "mz.waysNote": "What gets assigned is always a HOMEBASE article – never a bare article number. A new article is created with the product wizard, pre-filled with this version.",
  "mz.searchPlaceholder": "Search article number or description …",
  "mz.candidatesNote": "Only articles of this model's product family are offered. Blocked ones show the reason.",
  "mz.noCandidates": "No articles found.",
  "mz.blocked": "blocked",
  "mz.fits": "fits",
  "mz.fitsWithAdopt": "fits – missing values would be adopted",
  "mz.model": "Product model",
  "mz.execution": "Version",
  "mz.article": "Selected article",
  "mz.chkFamily": "Family matches",
  "mz.chkCategory": "Category matches",
  "mz.chkValues": "Version properties match",
  "mz.missingValues": "{n} missing on the article",
  "mz.chkOtherModel": "already assigned to another model",
  "mz.chkOtherExecution": "already another version",
  "mz.chkConflicts": "Conflicts",
  "mz.colExecution": "Version",
  "mz.colArticle": "Article",
  "mz.status.ok": "matches",
  "mz.status.conflict": "conflict",
  "mz.status.missing": "missing – can be adopted",
  "mz.status.unclear": "missing – not safely transferable",
  "mz.adoptMissing": "Create the missing values on the article from the version: {list}",
  "mz.why.adoptMissing": "Version values are missing on the article – please confirm adopting them.",
  "mz.assign": "Assign",
  "mz.replace": "Switch and assign",
  "mz.currentArticle": "Currently assigned:",
  "mz.confirmReplace": "Release the current assignment of {old} and assign {neu}",
  "mz.confirmReplaceNote": "{old} is NOT deleted – only its link to this version goes. The article number stays taken.",
  "mz.why.confirmReplace": "This version already carries an article – please confirm the switch explicitly.",
  "mz.back": "Choose another article",
  "mz.block.trashed": "The article is in the trash.",
  "mz.block.placeholder": "The article number is a placeholder.",
  "mz.block.family_mismatch": "The article does not belong to this model's product family.",
  "mz.block.category_mismatch": "The article's category does not match.",
  "mz.block.other_model": "The article already belongs to another product model – it is not moved.",
  "mz.block.other_execution": "The article is already assigned to another version.",
  "mz.block.execution_taken": "This version already has an article.",
  "mz.block.value_conflict": "A property of the article contradicts the version – nothing is overwritten.",
  "mz.block.not_main_product": "Accessories, components and spare parts are not a version – they belong to the model without a version.",
  "mz.block.value_unclear": "A missing value cannot be transferred safely from the version.",
  "mz.deleteOpen": "Delete product model …",
  "mz.deleteTitle": "Delete product model",
  "mz.deleteRemoves": "Deleting removes:",
  "mz.deleteModel": "the product model {code}",
  "mz.deleteExecutions": "{n} versions without article that belong only to this model",
  "mz.deleteAxes": "{n} version properties with {values} allowed values",
  "mz.deleteNoArticles": "NO articles, prices, documents or business data are deleted.",
  "mz.deleteHistory": "{n} history entries are kept.",
  "mz.deleteConfirm": "I want to delete {code} permanently.",
  "mz.deleteButton": "Delete permanently",
  "mz.notDeletable": "This product model cannot be deleted because business data is still attached:",
  "mz.archiveLater": "Archiving follows with PRODUCT MODELS 003.",
  "mz.dep.articles": "{n} assigned articles",
  "mz.dep.executions_with_article": "{n} versions with an article",
  "mz.dep.texts": "{n} model texts",
  "mz.dep.properties": "{n} model properties",
  "mz.dep.documents": "{n} documents",
  "mz.dep.packaging": "{n} packaging versions",
  "mz.dep.requests": "{n} information requests",
  "neu.fromExecution": "New article for version {execution}",
  "modelLog.execution_assigned": "Article assigned to version",
  "error.assignment_blocked": "This article cannot be assigned to the version.",
  "error.values_missing": "Version values are missing on the article – adopting them must be confirmed.",
  "error.execution_in_use": "The assignment cannot be removed because of existing business data.",
  "mp.statusEmpty": "No version has an article yet – the status appears as soon as a version has its article.",
  "mp.inTrash": "in the trash: {number}",
  "mz.replacesTrashed": "This version still holds {number} in the trash. A new assignment replaces it; restoring it will then not bring the version back.",
  /* Produktmodelle 002B -- Korrekturworkflow. */
  "mk.actions": "Actions",
  "mk.catalog": "Property catalogue",
  "mk.catalogNote": "If a property is missing, it is created here – and is usable in the model at once. Removing a property from this model is something else: that is what the “Shared value” switch in the row does.",
  "mk.newProperty": "+ New property",
  "mk.manageExisting": "Maintain existing property",
  "mk.code": "Technical key",
  "mk.codeHint": "Lower case, digits, underscores – e.g. dc_output_current. It cannot be changed later.",
  "mk.nameDe": "Name (German)",
  "mk.nameEn": "Name (English)",
  "mk.group": "Property group",
  "mk.valueType": "Data type",
  "mk.type.range": "Range or single value",
  "mk.type.integer": "Whole number",
  "mk.type.decimal": "Decimal number",
  "mk.type.text": "Text",
  "mk.type.boolean": "Yes/no",
  "mk.unit": "Unit",
  "mk.unitHint": "Symbol of a known unit, e.g. mA or V. Numeric properties only.",
  "mk.similar": "Similar properties already exist:",
  "mk.similarInUse": ", in use",
  "mk.create": "Create property",
  "mk.structure": "Structure",
  "mk.structureFree": "Data type and unit are still changeable",
  "mk.structureLocked": "Data type and unit are locked",
  "mk.confirmLabel": "Type {code} to confirm",
  "mk.delete": "Delete system-wide",
  "mk.openArticle": "Open article",
  "mk.change": "Change assignment …",
  "mk.unassignOpen": "Release assignment …",
  "mk.discardOpen": "Discard faulty creation …",
  "mk.unassignTitle": "Release article assignment",
  "mk.unassignText": "You are releasing {number} from this version.",
  "mk.unassignKeepsArticle": "The article is NOT deleted – it remains with all its data.",
  "mk.unassignKeepsNumber": "The article number stays taken.",
  "mk.unassignFree": "The version is then available for an article again.",
  "mk.unassignKeepData": "The article carries business data; it stays with the article: {list}.",
  "mk.unassignBlocked": "The assignment cannot be released: {list}. Please correct the existing article.",
  "mk.unassign": "Release assignment",
  "mk.bind.calculations": "calculations exist",
  "mk.bind.price_releases": "price releases exist",
  "mk.bind.offers": "offers exist",
  "mk.bind.stock": "stock exists",
  "mk.bind.incoming": "incoming supply exists",
  "mk.bind.dte_events": "DTE listing events exist",
  "mk.bind.dte_listed": "marked or listed in DTE",
  "mk.bind.no_article": "This version has no article.",
  "mk.discardTitle": "Discard faulty creation",
  "mk.discardSteps": "Discarding does the following, in order:",
  "mk.stepUnassign": "The assignment to the model version is released.",
  "mk.stepTrash": "The article is moved to the trash.",
  "mk.stepPurge": "The article is permanently deleted – with a tombstone in the log.",
  "mk.stepNumberFree": "Afterwards the article number {number} is free again.",
  "mk.discardLosing": "This also removes: {list}.",
  "mk.discardConfirmLabel": "Type {number} to confirm",
  "mk.discard": "Discard and delete permanently",
  "mkRef.calculations": "Calculations",
  "mkRef.price_releases": "Price releases",
  "mkRef.offers": "Offers",
  "mkRef.stock": "Stock levels",
  "mkRef.incoming": "Incoming supplies",
  "mkRef.dte_events": "DTE events",
  "mkRef.supplier_articles": "Supplier relations",
  "mkRef.properties": "Property values",
  "mkRef.texts": "Texts",
  "mkRef.documents": "Documents",
  "mp.kpiAssigned": "{n} of {total} versions linked to an article",
  "mp.kpiCompleteOf": "of {total} complete",
  "neu.reuseNumber": "Article number {number} was permanently deleted. I deliberately reuse it.",
  "modelLog.execution_unassigned": "Article assignment released",
  "modelLog.execution_reassigned": "Article of version changed",
  "modelLog.failed_creation_discarded": "Faulty creation discarded",
  "error.discard_blocked": "This article has operational history and cannot be discarded.",
  "plp.currency": "Currency",
  "plp.currencyLocked": "This order already carries prices. The currency can only be changed while no line carries a price — otherwise agreed amounts would silently mean something else.",
  "plp.currencyMissing": "This order has no currency yet. Please set it on the order first — a price without a currency is a number without a statement.",
  "plp.supplierLocked": "This order already carries lines of its current supplier. The partner can only be changed once no line depends on it.",
  "plp.orderValue": "Order value",
  "plp.noValue": "no price recorded",
  "plp.valueIncomplete": "Order value incomplete",
  "plp.valuePartial": "{n} line(s) without a price — the order value is still incomplete.",
  "plp.lineTotal": "Total",
  "plp.lineKind": "Kind of line",
  "plp.lineFromCatalog": "Article from HOMEBASE",
  "plp.lineFree": "Free line",
  "plp.lineFreeHint": "Free line — not linked to a HOMEBASE article",
  "plp.unitPriceIn": "Agreed price ({w})",
  "plp.removeLine": "remove",
  "plp.editAria": "Edit order",
  "plp.certaintyOnlyActual": "This event happens — it is recorded as actual.",
  "plp.certaintyOnlyConfirmed": "This date is promised, not performed — it is recorded as confirmed.",
  "error.number_required": "An order needs its DTE number. HOMEBASE issues none.",
  "error.invalid_po_number": "Invalid PO number. Please use the format PO70302 — purchasing uses the 7 number range.",
  "error.po_exists": "This order number is already recorded.",
  "error.po_not_empty": "This order already carries lines, dates or a consolidation. It is cancelled, not removed.",
  "error.po_supplier_locked": "This order already carries lines of its current supplier. The partner can only be changed once no line depends on it.",
  "error.po_currency_locked": "This order already carries prices. The currency can only be changed while no line carries a price.",
  "error.po_currency_required": "This order has no currency yet. Please set it on the order first — a price without a currency is a number without a statement.",
  "error.po_already_consolidated": "This order is already assigned to a consolidation.",
  "error.date_required": "A date is missing here.",
  "error.unknown_incoterm": "This incoterm is not in the catalogue.",
  "error.unknown_currency": "This currency is not in the catalogue.",
  "error.invalid_quantity": "An order line needs a whole quantity greater than zero.",
  "error.invalid_price": "The price is not a positive number.",
  "error.line_exists": "This article is already a line of this order.",
  "error.line_foreign_supplier": "This relation belongs to another supplier. An order only carries lines of its own partner.",
  "error.cn_closed": "This consolidation is closed for assignment and takes no further order.",
  "error.unknown_subject": "HOMEBASE does not know this object.",
  "error.unknown_milestone": "HOMEBASE does not know this milestone.",
  "error.invalid_review_stars": "A rating runs from one to five stars — or none at all.",
  "error.milestone_wrong_subject": "This milestone belongs to another object.",
  "error.milestone_not_manual": "This date lives on its own record and is maintained there.",
  "error.certainty_not_allowed": "This date status does not fit this milestone: an event happens, a due date is promised.",
  "error.milestone_superseded": "This entry has already been superseded. A superseded entry stays — the current one is changed.",
  "error.milestone_has_history": "This milestone already has a history. It is moved, not removed.",
  "error.forecast_not_storable": "Only confirmed and actual dates are stored. A forecast is computed.",
  "error.anchor_not_allowed": "A lead time cannot be anchored on this milestone.",
  "error.status_derived": "This state follows from the recorded events and is not set by hand. Only closing and discarding are manual.",
  "error.cn_not_handed_over": "Departed can only be recorded after the goods have been taken over by the carrier.",
  "error.cn_not_landed": "Handover to the Hamburg logistics partner is only possible after the shipment has arrived in Hamburg (HH landed).",
  "error.bank_no_account": "A bank account needs an IBAN or an account number. An IBAN on its own is enough.",
  "error.cn_not_delivered": "This consolidation has not been delivered yet. It is closed after the actual delivery.",
  "error.unknown_transport_mode": "HOMEBASE does not know this mode of transport.",
  "error.cn_orders_not_received": "Not all orders of this consolidation have arrived at the forwarder yet. Whatever does not travel is released from the consolidation first.",
  "error.group_missing": "This document group does not exist for an order.",
  "error.cn_empty": "This consolidation holds no active order. An empty consolidation is not handed to the carrier.",
  "error.cn_in_transport": "This consolidation has already been handed to the carrier. It can no longer be changed.",
  "error.release_reason_required": "Releasing needs a reason. Without one it would be a deletion by another name.",
  "error.already_handed_over": "This order has already been handed over to the forwarder. Correcting it is a rebooking, not a release.",
  "error.cn_handed_over": "Goods from this consolidation have already been handed over. It can no longer be discarded the normal way.",
  "error.anchor_deprecated": "This milestone is no longer recorded. A new lead time rule cannot be anchored on it; existing rules remain valid.",
  "error.rule_scope": "A lead time rule applies to exactly one scope: supplier, relation or order.",
  "error.rule_days": "The duration needs a minimum greater than zero, and the maximum is not below it.",
  "error.unknown_source": "This source of a lead time statement does not exist.",
  "sup.roles": "Roles",
  "sup.roleAll": "All",
  "sup.roleFilter": "Filter by role",
  "partnerRole.lieferant": "Supplier",
  "partnerRole.forwarder": "Forwarder",
  /* Two logistics partners per shipment (0086). See the German file. */
  "plp.originForwarder": "Logistics partner Far East",
  "plp.destForwarder": "Logistics partner Hamburg",
  "plp.chooseDestForwarder": "Choose Hamburg logistics partner",
  "plp.noDestForwarder": "no Hamburg logistics partner",
  /* Correction run 005 -- see the German file. */
  "plp.noOpenProcessData": "No open process data.",
  "plp.groupPlanned": "Expected",
  "plp.groupActual": "Actual",
  "plp.stepNeedsFirst": "{was} can only be recorded once “{erst}” is on record.",
  "plp.intakeWindowClosedLegacy": "CLOSED (legacy record)",
  /* CN UX simplification 001 -- see the German file. */
  "plp.whatNow": "What needs doing now?",
  "plp.noOpenAction": "No open action.",
  "plp.nextStep": "Next",
  "plp.recordNow": "Record now",
  "plp.intakeMissing": "{n} orders have not yet arrived at the forwarder.",
  "plp.intakeMissingOne": "One order has not yet arrived at the forwarder.",
  "plp.recordIntake": "Record arrival",
  "plp.notPlanned": "not yet planned",
  "plp.legStart": "Departure Far East",
  "plp.legHamburg": "Hamburg",
  "plp.legLueneburg": "Lüneburg",
  "plp.colExpected": "Expected",
  "plp.colActual": "Actual",
  "plp.consolidation": "Consolidation",
  "plp.consolidationOpenHint": "Further orders can still join this shipment.",
  "plp.consolidationClosedHint": "Handed over to carrier on {tag}.",
  "plp.consolidationClosedLegacyHint": "Legacy record — consolidation is demonstrably over.",
  "plp.processTrail": "Process trail",
  "plp.trailDone": "{n} of {alle} process steps done",
  "plp.trailNext": "next step",
  "plp.planVsActual": "Plan vs. actual",
  "plp.waitingTimes": "Waiting times",
  "plp.goodsTitle": "Goods included",
  "plp.goodsFor": "in {cn}",
  "plp.goodsEmpty": "No goods assigned to this shipment yet.",
  "plp.goodsPieces": "{n} pcs",
  "plp.goodsNoArticle": "{n} line(s) without a greenius article number — see the order.",
  "plp.goodsHint": "Quantities combined across all orders of this shipment.",
  "plp.goodsOpenFile": "Full shipment file",
  "help.timeline.title": "Reading the timeline",
  "help.timeline.body": "A solid green box has happened — the event took place. A box with a green outline is planned or confirmed, but has not happened yet. A dashed outline is calculated: it comes from a lead time rule, nobody promised it. The faintly shaded rows are shipments; the orders above them feed into them.",
  "help.cn.transport.title": "Expected and actual",
  "help.cn.transport.body": "The left column says when something is expected, the right one when it actually happened. ETD is the planned departure, Departed the real one. ETA Hamburg is the expected arrival, HH landed the actual one. ETA Lüneburg and LG landed cover the final delivery. As long as the right column says “Still open”, the event has not happened.",
  "help.po.documents.title": "The documents of an order",
  "help.po.documents.body": "The Purchase Order shows what greenius ordered. The Proforma Invoice shows what the supplier confirms and charges. The Packing List shows which goods were actually prepared for shipping. All three belong to the order and are versioned: a new revision does not overwrite the old one, it supersedes it.",
  "pod.title": "Documents",
  "pod.po": "Purchase Order",
  "pod.pi": "Proforma Invoice",
  "pod.packing": "Packing List",
  "pod.present": "present",
  "pod.missing": "required · missing",
  "pod.notYet": "not required yet",
  "pod.upload": "Upload",
  "pod.replace": "New revision",
  "pod.uploading": "storing …",
  "pod.versions": "Earlier revisions ({n})",
  "pod.uploadedOn": "stored on {datum}",
  "pod.documentDate": "document date {datum}",
  "pod.supersededNote": "Earlier revisions are kept — a new revision does not replace them, it supersedes them.",
  "pod.ofOrders": "Documents of the orders",
  "pod.cnReadOnly": "The documents belong to the orders. They are only shown here — upload happens in the order file.",
  "pod.noOrders": "No order is assigned to this shipment.",
  "help.modeOn": "Turn on help",
  "help.modeOff": "Turn off help",
  "help.ov.cnwahl.title": "Which shipment you are looking at",
  "help.ov.cnwahl.body": "Pick the shipment at the top. The timeline and the incoming goods then show exactly that shipment and its orders — the others are hidden. “What needs attention?” deliberately stays complete: an urgent point should not disappear because a different shipment is selected.",
  "help.ov.ware.title": "What arrives with this shipment",
  "help.ov.ware.body": "These are the goods of the whole shipment, not of a single order. If the same article comes from several orders, the quantities are added up. Which order contributes what is shown in the shipment file.",
  "help.cn.arbeitszone.title": "What needs doing now",
  "help.cn.arbeitszone.body": "HOMEBASE shows the next step for this shipment — not every possible one. If a step is shown as waiting, a precondition is still missing; it is named next to it.",
  "help.cn.planist.title": "Plan vs. actual",
  "help.cn.planist.body": "The deviation between the expected and the actual date, in days. It says WHAT happened — not who caused it. A late departure may well have been a deliberate decision.",
  "help.po.incoterm.title": "Incoterm",
  "help.po.incoterm.body": "The Incoterm defines from which point greenius carries the cost and risk of transport. EXW means ex works, FOB from the port of loading, DAP to the agreed destination.",
  "help.po.leadtime.title": "Lead time anchor",
  "help.po.leadtime.body": "The supplier's promised lead time starts counting from this event. Without the anchor no forecast can be calculated — a duration without a starting point says nothing.",
  "help.po.payments.title": "Payments and evidence",
  "help.po.payments.body": "These are the payments belonging to this order and their payment evidence. Deposit, partial and balance payments are separate entries; the evidence — a bank slip or payment confirmation — belongs to the specific payment, not to the order.",
  "plp.unassigned": "Unassigned",
  "plp.shipments": "Shipments",
  "plp.etaLueneburg": "ETA Lüneburg {datum}",
  "pay.evidence": "Payment evidence",
  "pay.evidenceMissing": "evidence missing",
  "pay.evidenceUpload": "Upload evidence",
  "pay.evidenceNew": "New revision",
  "plp.trailBlocked": "only possible after “{erst}”",
  "plp.needsIntake": "all orders received by the forwarder",
  "plp.editMilestones": "Edit milestones",
  "plp.deltaDays": "{n} days",
  "plp.deltaEarly": "{n} days early",
  "plp.deltaOnTime": "on time",
  "plp.intakeWindowOpen": "OPEN",
  "plp.intakeWindowClosed": "CLOSED",
  "plp.legacyUndocumented": "not documented (legacy record)",
  "plp.intakeLegacyHint": "Arrival at the forwarder not documented (legacy record) — this field did not exist when the shipment was created.",
  "plp.chooseForwarder": "Choose Far East logistics partner",
  "plp.noForwarder": "no Far East logistics partner",
  "plp.manageForwarders": "Manage logistics partners",
  "plp.forwarderFromPartners": "The list shows business partners carrying the forwarder role. Further ones can be added under Business Partners.",
  "plp.noForwarderYet": "No business partner carries the forwarder role yet. Add the role to an existing organisation under Business Partners — or create a new one there.",
  "error.unknown_partner_role": "This partner role is not in the catalogue.",
  "error.roles_required": "A business partner needs at least one role — supplier, forwarder or both. Without one it would appear in no list.",
  "error.partner_role_in_use": "This role cannot be removed: a consolidation lists this organisation as its forwarder. Please release that assignment first.",
  "error.unknown_forwarder": "This logistics partner does not exist.",
  "error.not_a_supplier": "This business partner does not carry the supplier role. It can be added in its file — an organisation may have several roles.",
  "error.not_a_forwarder": "This business partner does not carry the forwarder role. It can be added in its file — an organisation may have several roles.",
  "plp.uploadOrder": "Upload order",
  "plp.manualOrder": "Enter manually",
  "plp.uploadHint": "The PDF is evidence, not confirmed truth. HOMEBASE reads a proposal from it; the order is created only once you have checked and confirmed it.",
  "plp.captureTitle": "Take order from document",
  "plp.captureFindings": "What HOMEBASE saw in the document",
  "plp.captureSource": "Document",
  "plp.captureSourceValue": "{datei} — uploaded by {wer} on {wann}",
  "plp.captureRawNumber": "Order number in the document",
  "plp.captureNothing": "not recognised",
  "plp.captureClean": "All header fields and all lines were read. Please still check against the document — what is confirmed is what stands here.",
  "plp.captureDuplicate": "Order {nr} is already recorded. HOMEBASE creates no second one and does not change the existing one.",
  "plp.captureOpenExisting": "open the existing order",
  "plp.captureSupplierOne": "The document says “{name}” — one business partner matches and is preselected.",
  "plp.captureSupplierMany": "The document says “{name}” — {n} business partners match. Please choose.",
  "plp.captureSupplierNone": "The document says “{name}” — no business partner matches. Please choose one or create it first.",
  "plp.captureRawItem": "in the document: {nr}",
  "plp.captureAmbiguous": "several matches",
  "plp.noArticle": "— no article (cost line) —",
  "plp.lineDescription": "Description",
  "plp.unit": "Unit",
  "plp.documentTotal": "Total per document",
  "plp.documentTotalHint": "The amount stated in the document. It does not replace the computed order value.",
  "plp.totalsDiffer": "The lines add up to {summe}, the document states {beleg} — a difference of {rest}. HOMEBASE aligns nothing and assumes neither discount nor freight, fee or tax. Please check.",
  "plp.confirmOrder": "Confirm order",
  "plp.confirmHint": "The order is created only on confirmation. Number, supplier and document date are required for it.",
  "beleg.kein_text": "No text could be read from this PDF — probably a scan. The fields stay empty; please enter manually.",
  "beleg.verschluesselt": "This PDF is encrypted and cannot be read.",
  "beleg.keine_nummer": "No order number recognised.",
  "beleg.kein_datum": "No document date recognised.",
  "beleg.kein_lieferant": "No supplier name recognised.",
  "beleg.keine_waehrung": "No currency recognised.",
  "beleg.kein_gesamtwert": "No total recognised in the document.",
  "beleg.keine_positionen": "No lines recognised.",
  "beleg.keine_tabelle": "No line table found.",
  "beleg.summe_weicht_ab": "The sum of the lines and the document total differ.",
  "beleg.menge_fehlt": "quantity not recognised",
  "beleg.menge_abgeleitet": "quantity derived from amount and price",
  "beleg.preis_fehlt": "price not recognised",
  "beleg.wert_fehlt": "line amount not recognised",
  "beleg.rechnung_geht_nicht_auf": "quantity × price does not give the line amount",
  "beleg.keine_artikelnummer": "no item number in the document",
  "error.beleg_kein_pdf": "HOMEBASE currently accepts a PDF as an order document.",
  "error.capture_not_open": "This capture has already been completed.",
  "error.line_needs_subject": "A line needs an article or a description. An amount without a subject is not an order line.",
  "error.invalid_document_total": "The document total is not a number.",
  "plp.finance": "Finance",
  "plp.paymentTerms": "Payment agreement",
  "plp.termsWhereEdited": "The agreement is maintained where it comes from — at the supplier or at the article relation.",
  "plp.noPaymentTerms": "No payment agreement on file — neither for this order nor for the supplier.",
  "plp.termsFromOrder": "agreed for this order",
  "plp.termsTakenOver": "taken over from the supplier default",
  "plp.termsFromArticle": "from the article relation",
  "plp.termsFromSupplier": "supplier default",
  "plp.paidOn": "Payment date",
  "plp.paidOnHint": "When it was paid — not when you record it here. Cut-off reports use this date.",
  "plp.paymentKind": "Kind",
  "plp.paymentAmount": "Amount",
  "plp.paymentAmountIn": "Amount ({w})",
  "plp.paymentNote": "Note",
  "plp.paymentReference": "Reference",
  "plp.noPayments": "No payment recorded yet.",
  "plp.paidTotal": "Paid in total",
  "plp.stillOpen": "Still open",
  "plp.addPayment": "+ Record payment",
  "plp.removePayment": "remove",
  "plp.recordedOn": "recorded by {wer} on {wann}",
  "plp.foreignCurrency": "other currency",
  "plp.financeSeparation": "Paid is not fulfilled: whether the goods have arrived is told by the dates — not by this section.",
  "payKind.deposit": "Deposit",
  "payKind.partial": "Partial payment",
  "payKind.balance": "Balance payment",
  "payKind.other": "other",
  "finBefund.ueberzahlt": "More was paid than ordered. HOMEBASE does not correct this — please check.",
  "finBefund.fremdwaehrung": "At least one payment is in a different currency than the order. It is not converted and does not count towards “Paid in total”.",
  "finBefund.kein_bestellwert": "Payments are recorded but the order has no value — the open amount cannot be formed.",
  "finBefund.keine_waehrung": "The order has no currency.",
  "finBefund.zahlung_vor_bestellung": "At least one payment predates the order date. That may be correct — please check.",
  "finBefund.bezahlt_ohne_wareneingang": "Fully paid, but no goods receipt recorded yet. Paid is not fulfilled.",
  "finBefund.ware_da_rest_offen": "The goods have arrived, a balance is still open.",
  "finBefund.bestellwert_unvollstaendig": "At least one line has no price. The order value is calculated correctly, but it is not yet the whole order.",
  "finBefund.wert_nicht_rekonstruierbar": "The order value for this cut-off date cannot be reconstructed: at least one line was removed before HOMEBASE recorded its quantity and price. The value stays empty — nothing is guessed.",
  "ms.eta_hamburg": "ETA Hamburg",
  /* ETA HH is the expectation, HH landed the event. */
  "ms.hh_landed": "HH landed",
  "ms.customs_cleared": "Customs released",
  "ms.eta_lueneburg": "ETA Lüneburg",
  /* Departure is not handover -- see the German file. */
  "ms.departed": "Departed",
  "ms.hh_handover": "Handed over to Hamburg logistics partner",
  /* Corrected -- see the German file. */
  "ms.carrier_handover": "Handed over to carrier",
  "ms.goods_delivered": "LG landed",
  "ms.goods_received": "Goods received",
  "ms.packing_list_received": "Packing List received",
  "ms.freight_requested": "Freight request sent",
  "ms.freight_quoted": "Freight quote received",
  "ms.freight_ordered": "Freight order placed",
  /* Spelled out for the same reason as in German: a bare "goods taken
     over" would read as the goods receipt at greenius. */
  "ms.goods_handed_over": "Goods taken over by logistics partner",
  "ms.customs_docs_sent": "CI & Packing List submitted for customs clearance",
};
